import { 
  BookOpen, 
  Target, 
  Users, 
  TrendingUp, 
  FileText, 
  Trophy, 
  Download,
  MessageSquare,
  Globe,
  PenTool,
  Search,
  AlertTriangle,
  Mail,
  Award
} from 'lucide-react';

const ORANGE_LIGHTBULB_ICON = `
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
    <path d="M12 2a6 6 0 0 0-6 6c0 2.2 1.1 4.2 2.9 5.4l.1.1a2 2 0 0 1 .9 1.6V17h4v-1.9a2 2 0 0 1 .9-1.6l.1-.1C17 12.2 18 10.2 18 8a6 6 0 0 0-6-6z"/>
    <path d="M9 18h6"/>
    <path d="M10 22h4"/>
  </svg>
`;

// Reusable template for case studies/real stories
const createCaseStudy = (title: string, story: string, lesson?: string) => {
  const lightbulbIcon = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 2a6 6 0 0 0-6 6c0 2.2 1.1 4.2 2.9 5.4l.1.1a2 2 0 0 1 .9 1.6V17h4v-1.9a2 2 0 0 1 .9-1.6l.1-.1C17 12.2 18 10.2 18 8a6 6 0 0 0-6-6z"/>
      <path d="M9 18h6"/>
      <path d="M10 22h4"/>
    </svg>
  `;
  
  const lessonSection = lesson 
    ? `<div class="story-takeaway"><strong>The lesson:</strong> ${lesson}</div>`
    : '';
  
  return `
    <div class="story-highlight" style="margin: 2rem 0;">
      <div class="story-content">
        <h4>
          ${lightbulbIcon}
          ${title}
        </h4>
        <p>${story}</p>
        ${lessonSection}
      </div>
    </div>
  `;
};

// Reusable template for simple case insights (without lesson)
const createCaseInsight = (title: string, content: string) => {
  return `
    <div class="story-highlight">
      <div class="story-content">
        <h4>${title}</h4>
        <p>${content}</p>
      </div>
    </div>
  `;
};

export const module1Config = {
  title: "Introduction to Principal IT Talent Challenges",
  description: "Module 1: Introduction to Principal IT Talent Challenges (Free Version)",
  duration: "20 min",
  sections: [
    {
      id: -1,
      title: "Before You Start: Market Introduction",
      shortTitle: "Before Start",
      icon: Award,
      content: "beforeStart"
    },
    {
      id: 0,
      title: "Welcome to Principal IT Talent Challenges",
      shortTitle: "Welcome",
      icon: BookOpen,
      content: "welcome"
    },
    {
      id: 1,
      title: "Why Principals Drive Company Growth",
      shortTitle: "WHYs",
      icon: Target,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Principal IT professionals - those with 10+ years of experience - are the backbone of innovation in tech companies. 
          They lead critical projects, like building AI platforms or securing cybersecurity frameworks, and mentor teams to drive success.
        </p>
        
        <div class="stats-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; margin: 2rem 0;">
          <div class="stat-card" style="background: white; border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); text-align: center;">
            <div class="stat-number" style="font-size: 2rem; font-weight: 700; color: var(--orange); margin-bottom: 0.5rem;">63%</div>
            <div class="stat-label" style="font-size: 0.9rem; color: var(--gray);">Companies struggling with senior hiring</div>
          </div>
          <div class="stat-card" style="background: white; border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); text-align: center;">
            <div class="stat-number" style="font-size: 2rem; font-weight: 700; color: var(--orange); margin-bottom: 0.5rem;">€80k-120k</div>
            <div class="stat-label" style="font-size: 0.9rem; color: var(--gray);">Average senior salary in CZ</div>
          </div>
          <div class="stat-card" style="background: white; border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); text-align: center;">
            <div class="stat-number" style="font-size: 2rem; font-weight: 700; color: var(--orange); margin-bottom: 0.5rem;">15,000+</div>
            <div class="stat-label" style="font-size: 0.9rem; color: var(--gray);">New IT jobs projected 2025</div>
          </div>
        </div>

        ${createCaseStudy(
          "Real Story: Cybersecurity Specialists",
          "A leading cybersecurity firm needed Malware Analysts — highly mathematical experts who could dive into low-level programming at the kernel level. These candidates were incredibly rare, and neither LinkedIn nor job boards were enough. By tapping into niche tech communities and leveraging my network, I found five exceptional analysts who helped strengthen the company's global reputation.",
          "When you know your stuff and look beyond the usual channels, you can find the talent others miss."
        )}
      `
    },
    {
      id: 2,
      title: "CZ/US Market Stats, Salaries, Shortages",
      shortTitle: "Market",
      icon: TrendingUp,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Hiring senior IT talent in the US and Czech Republic is tough. With unemployment at just 2-3%, top professionals have their pick of jobs.
        </p>

        <div class="challenges-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; margin: 2rem 0;">
          <div class="challenge-card" style="background: white; border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <div class="challenge-card-header" style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              <h4 style="font-size: 1.1rem; font-weight: 600; color: var(--dark); margin: 0;">Salary Competition</h4>
            </div>
            <p style="color: var(--gray); line-height: 1.6; margin: 0;">Senior developers can earn $150,000-250,000 in the US versus €80,000-120,000 in Czech Republic.</p>
          </div>
          <div class="challenge-card" style="background: white; border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <div class="challenge-card-header" style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="20" x2="18" y2="10"></line>
                <line x1="12" y1="20" x2="12" y2="4"></line>
                <line x1="6" y1="20" x2="6" y2="14"></line>
              </svg>
              <h4 style="font-size: 1.1rem; font-weight: 600; color: var(--dark); margin: 0;">Application Overload</h4>
            </div>
            <p style="color: var(--gray); line-height: 1.6; margin: 0;">Sometimes 100+ applicants per role, but most lack specialised skills needed for principal positions.</p>
          </div>
          <div class="challenge-card" style="background: white; border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <div class="challenge-card-header" style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
              </svg>
              <h4 style="font-size: 1.1rem; font-weight: 600; color: var(--dark); margin: 0;">Training Gaps</h4>
            </div>
            <p style="color: var(--gray); line-height: 1.6; margin: 0;">Many TA teams lack deep recruitment experience, coming from HR or office management roles.</p>
          </div>
          <div class="challenge-card" style="background: white; border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <div class="challenge-card-header" style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="22,7 22,1 16,1"></polyline>
                <polyline points="2,17 2,23 8,23"></polyline>
                <polyline points="16,1 22,7 16,13"></polyline>
                <polyline points="8,23 2,17 8,11"></polyline>
              </svg>
              <h4 style="font-size: 1.1rem; font-weight: 600; color: var(--dark); margin: 0;">Market Predictions</h4>
            </div>
            <p style="color: var(--gray); line-height: 1.6; margin: 0;">Czech Republic: 3.5 job seekers per vacancy. US: 8.8M openings with 4.3-4.5% unemployment rates.</p>
          </div>
        </div>

        <div class="unemployment-reports" style="margin: 3rem 0;">
          <div class="report-card" style="background: white; border-radius: 12px; padding: 2rem; margin-bottom: 2rem; border-left: 4px solid var(--orange); box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <h4 style="font-size: 1.25rem; font-weight: 600; color: var(--dark); margin-bottom: 1rem; font-family: var(--montserrat);">
              Short Report: Unemployment in the Czech Republic (Focus on IT Sector, 2025)
            </h4>
            
            <div style="margin-bottom: 1.5rem;">
              <h5 style="font-size: 1rem; font-weight: 600; color: var(--dark); margin-bottom: 0.5rem;">Monthly Overall Unemployment (2025 Avg: ~4.3%):</h5>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 1rem;">
                Started at 4.3% (Jan), dipped to 4.2% (May-Jun), rose to 4.5% (Aug-Sep). Industrial slowdowns drove increases, but services remained stable.
              </p>
            </div>

            <div style="margin-bottom: 1.5rem;">
              <h5 style="font-size: 1rem; font-weight: 600; color: var(--dark); margin-bottom: 0.5rem;">IT Sector Monthly Highlights (Avg: ~0.6%):</h5>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 1rem;">
                Rates stayed low (0.5-0.7%), with minimal job seekers (~4,000/month) and high vacancies (2.5+ per worker). Demand in AI/cybersecurity offset any pressures; Prague hubs at ~0.4%.
              </p>
            </div>

            <div>
              <h5 style="font-size: 1rem; font-weight: 600; color: var(--dark); margin-bottom: 0.5rem;">Quarterly Comparison (Overall Rates):</h5>
              <div class="quarterly-table" style="background: rgba(255, 255, 255, 0.5); border-radius: 8px; padding: 1rem; margin-bottom: 1rem;">
                <table style="width: 100%; font-size: 0.9rem;">
                  <thead>
                    <tr style="border-bottom: 1px solid #e5e7eb;">
                      <th style="padding: 0.5rem; text-align: left; font-weight: 600;">Quarter</th>
                      <th style="padding: 0.5rem; text-align: center; font-weight: 600;">2023</th>
                      <th style="padding: 0.5rem; text-align: center; font-weight: 600;">2024</th>
                      <th style="padding: 0.5rem; text-align: center; font-weight: 600;">2025</th>
                      <th style="padding: 0.5rem; text-align: center; font-weight: 600;">Change (2025 vs. 2024)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style="padding: 0.5rem; font-weight: 500;">Q1</td>
                      <td style="padding: 0.5rem; text-align: center;">3.7%</td>
                      <td style="padding: 0.5rem; text-align: center;">3.9%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.3%</td>
                      <td style="padding: 0.5rem; text-align: center;">+0.4 pp</td>
                    </tr>
                    <tr>
                      <td style="padding: 0.5rem; font-weight: 500;">Q2</td>
                      <td style="padding: 0.5rem; text-align: center;">3.8%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.0%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.2%</td>
                      <td style="padding: 0.5rem; text-align: center;">+0.2 pp</td>
                    </tr>
                    <tr>
                      <td style="padding: 0.5rem; font-weight: 500;">Q3</td>
                      <td style="padding: 0.5rem; text-align: center;">3.9%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.1%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.5%</td>
                      <td style="padding: 0.5rem; text-align: center;">+0.4 pp</td>
                    </tr>
                    <tr style="border-top: 1px solid #e5e7eb; font-weight: 600;">
                      <td style="padding: 0.5rem;">Annual</td>
                      <td style="padding: 0.5rem; text-align: center;">3.8%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.0%</td>
                      <td style="padding: 0.5rem; text-align: center;">~4.3%</td>
                      <td style="padding: 0.5rem; text-align: center;">+0.3 pp</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style="color: var(--gray); line-height: 1.6; font-size: 0.9rem; font-style: italic;">
                IT sector stable at ~0.6% across years, supporting tight market for talent.
              </p>
            </div>
          </div>

          <div class="report-card" style="background: white; border-radius: 12px; padding: 2rem; border-left: 4px solid var(--orange); box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <h4 style="font-size: 1.25rem; font-weight: 600; color: var(--dark); margin-bottom: 1rem; font-family: var(--montserrat);">
              Short Report: Unemployment in the United States (Focus on IT Sector, 2025)
            </h4>
            
            <div style="margin-bottom: 1.5rem;">
              <h5 style="font-size: 1rem; font-weight: 600; color: var(--dark); margin-bottom: 0.5rem;">Monthly Overall Unemployment (2025 Avg: ~4.2%):</h5>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 1rem;">
                Rose from 4.0% (Jan) to 4.3% (Aug), with a dip to 4.1% (Jun). Slower job growth (~100k/month) amid economic cooling.
              </p>
            </div>

            <div style="margin-bottom: 1.5rem;">
              <h5 style="font-size: 1rem; font-weight: 600; color: var(--dark); margin-bottom: 0.5rem;">IT Sector Monthly Highlights (Avg: ~4.7%):</h5>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 1rem;">
                Rates varied (4.0-5.5%), higher than national due to tech adjustments, but resilient with ~2% employment growth. High demand for AI roles; ~317k annual openings despite skills gaps.
              </p>
            </div>

            <div>
              <h5 style="font-size: 1rem; font-weight: 600; color: var(--dark); margin-bottom: 0.5rem;">Quarterly Comparison (Overall Rates):</h5>
              <div class="quarterly-table" style="background: rgba(255, 255, 255, 0.5); border-radius: 8px; padding: 1rem; margin-bottom: 1rem;">
                <table style="width: 100%; font-size: 0.9rem;">
                  <thead>
                    <tr style="border-bottom: 1px solid #e5e7eb;">
                      <th style="padding: 0.5rem; text-align: left; font-weight: 600;">Quarter</th>
                      <th style="padding: 0.5rem; text-align: center; font-weight: 600;">2023</th>
                      <th style="padding: 0.5rem; text-align: center; font-weight: 600;">2024</th>
                      <th style="padding: 0.5rem; text-align: center; font-weight: 600;">2025</th>
                      <th style="padding: 0.5rem; text-align: center; font-weight: 600;">Change (2025 vs. 2024)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style="padding: 0.5rem; font-weight: 500;">Q1</td>
                      <td style="padding: 0.5rem; text-align: center;">3.5%</td>
                      <td style="padding: 0.5rem; text-align: center;">3.8%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.1%</td>
                      <td style="padding: 0.5rem; text-align: center;">+0.3 pp</td>
                    </tr>
                    <tr>
                      <td style="padding: 0.5rem; font-weight: 500;">Q2</td>
                      <td style="padding: 0.5rem; text-align: center;">3.5%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.0%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.2%</td>
                      <td style="padding: 0.5rem; text-align: center;">+0.2 pp</td>
                    </tr>
                    <tr>
                      <td style="padding: 0.5rem; font-weight: 500;">Q3</td>
                      <td style="padding: 0.5rem; text-align: center;">3.7%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.2%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.3%</td>
                      <td style="padding: 0.5rem; text-align: center;">+0.1 pp</td>
                    </tr>
                    <tr style="border-top: 1px solid #e5e7eb; font-weight: 600;">
                      <td style="padding: 0.5rem;">Annual</td>
                      <td style="padding: 0.5rem; text-align: center;">3.6%</td>
                      <td style="padding: 0.5rem; text-align: center;">4.0%</td>
                      <td style="padding: 0.5rem; text-align: center;">~4.2%</td>
                      <td style="padding: 0.5rem; text-align: center;">+0.2 pp</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style="color: var(--gray); line-height: 1.6; font-size: 0.9rem; font-style: italic;">
                IT sector ~4.7% in 2025, up slightly from 2023-2024 (~4.5-4.6%), reflecting competition for skilled hires.
              </p>
            </div>
          </div>
        </div>

        ${createCaseStudy(
          "Real Story: High-Tech C++ Developer",
          "A high-tech company needed a C++ developer who could pass their rigorous interview process. Most recruiters struck out, but by digging into niche developer forums and understanding the company's technical needs, I found a candidate who not only got the offer but became a key contributor.",
          "With the right approach, even the toughest roles can be filled."
        )}
      `
    },
    {
      id: 3,
      title: "Creative Sourcing Beyond LinkedIn",
      shortTitle: "Sourcing",
      icon: Search,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Traditional job boards and LinkedIn won't cut it for principal talent. You need creative sourcing strategies that go beyond the obvious channels.
        </p>

        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem; margin: 2rem 0;">
          <div class="challenge-card" style="background: white; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <div class="challenge-icon" style="width: 48px; height: 48px; background: rgba(216, 183, 37, 0.1); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="6"></circle>
                <path d="M12 2v4"></path>
                <path d="M12 18v4"></path>
                <path d="M2 12h4"></path>
                <path d="M18 12h4"></path>
              </svg>
            </div>
            <div class="challenge-content">
              <h3 style="font-size: 1.25rem; font-weight: 600; color: var(--dark); margin-bottom: 0.75rem;">GitHub & Open Source Communities</h3>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 0.75rem;">
                <strong>GitHub Repos:</strong> Find contributors to relevant projects. Example: For cybersecurity roles, I searched GitHub for kernel-level security contributions.
              </p>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 0.75rem;">
                <strong>Niche Forums:</strong> Stack Overflow, Reddit (r/cscareerquestions), Hacker News. Engage authentically - don't just post job ads.
              </p>
              <p style="color: var(--gray); line-height: 1.6; margin: 0;">
                <strong>Developer Meetups:</strong> Prague Devs, Czech ICT Summit (CZ), TechCrunch Disrupt, IEEE events (US). Build relationships before you need to hire.
              </p>
            </div>
          </div>
          
          <div class="challenge-card" style="background: white; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <div class="challenge-icon" style="width: 48px; height: 48px; background: rgba(216, 183, 37, 0.1); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div class="challenge-content">
              <h3 style="font-size: 1.25rem; font-weight: 600; color: var(--dark); margin-bottom: 0.75rem;">Referral Programs</h3>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 0.75rem;">
                <strong>Internal Referrals:</strong> Offer €2,000 (CZ) or $5,000 (US) bonuses for successful principal hires. Your best engineers know other great engineers.
              </p>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 0.75rem;">
                <strong>External Networks:</strong> Tap into former colleagues, university alumni networks, and industry associations.
              </p>
              <p style="color: var(--gray); line-height: 1.6; margin: 0;">
                <strong>Pro Tip:</strong> Don't just ask for referrals - offer value first. Share market insights or help with their own career questions.
              </p>
            </div>
          </div>
          
          <div class="challenge-card" style="background: white; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
            <div class="challenge-icon" style="width: 48px; height: 48px; background: rgba(216, 183, 37, 0.1); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2a6 6 0 0 0-6 6c0 2.2 1.1 4.2 2.9 5.4l.1.1a2 2 0 0 1 .9 1.6V17h4v-1.9a2 2 0 0 1 .9-1.6l.1-.1C17 12.2 18 10.2 18 8a6 6 0 0 0-6-6z"/>
                <path d="M9 18h6"/>
                <path d="M10 22h4"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3 style="font-size: 1.25rem; font-weight: 600; color: var(--dark); margin-bottom: 0.75rem;">AI-Powered Tools</h3>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 0.75rem;">
                <strong>Screening Automation:</strong> Use AI tools to filter resumes and identify candidates with niche skills. Can save up to 80% of screening time.
              </p>
              <p style="color: var(--gray); line-height: 1.6; margin-bottom: 0.75rem;">
                <strong>Boolean Search Strings:</strong> Master advanced LinkedIn and GitHub searches. The PDFs you will download in the following modules include ready-to-use strings for AI, cybersecurity, and DevOps roles.
              </p>
              <p style="color: var(--gray); line-height: 1.6; margin: 0;">
                <strong>Remember:</strong> AI helps with efficiency, but personal engagement still wins. Use tools to find candidates, then build relationships authentically.
              </p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 4,
      title: "Interactive Reflection Activity (you need the PDFs)",
      shortTitle: "Challenges",
      icon: FileText,
      content: "activity"
    },
    {
      id: 5,
      title: "Quiz – 5 questions taken verbatim from the checklists",
      shortTitle: "Quiz",
      icon: Trophy,
      content: "quiz"
    },
    {
      id: 6,
      title: "Takeaway: Market Insights Hacker Kit",
      shortTitle: "Takeaways",
      icon: Download,
      content: "takeaway"
    }
  ],
  resources: [
    {
      title: "Czech IT Talent Market 2025 Checklist",
      type: "pdf" as const,
      url: "czech-it-talent-market-2025-checklist.pdf",
      downloadable: true
    },
    {
      title: "US IT Talent Market 2025 Checklist",
      type: "pdf" as const,
      url: "us-it-talent-market-2025-checklist.pdf",
      downloadable: true
    }
  ],
  pdfTeaser: {
    title: "Before You Start",
    subtitle: "Most recruiters guess the market and lose seniors to competitors.",
    description: "The Market Insights Hacker Kit (two checklists: Czech + US 2025) you're about to download is the exact data pack we use when sourcing for our clients. Without it → outdated assumptions. With it → you speak the market like a pro and spot opportunities others miss.",
    urgency: {
      text: "Required for the reflection activity.",
      bonus: "Limited-time bonus: download within 24 hours and get a free 15-minute market audit with me (normally reserved for private clients)."
    },
    teasers: {
      title: "Exact preview – taken word-for-word from your PDFs (no hype, only what's really inside):",
      items: [
        "Key Stats Teaser: CZ ~15,000 new IT jobs 2025, 63% shortage, €60K–€100K principals; US 317,700 openings, 74% shortage, $150K–$250K total comp",
        "Principal Identification Checklist (both PDFs): 10+ years niche expertise, leadership proof, motivators (impact/autonomy CZ, equity/growth US)",
        "Creative Sourcing Strategies: GitHub repos, niche forums (e.g., cybersecurity specialist hires via universities/forums – none on LinkedIn), referrals (€2K/$5K bonuses), AI tools (80% screening time saved)",
        "Salary/Negotiation Tips: CZ counter US offers with hybrid + learning budgets; US emphasize equity + fast offers",
        "Regional Comparisons: CZ tight 2.7% unemployment vs. US 4.3%, hybrid prefs, undertrained TA teams",
        "Case Insights: cybersecurity firms sourcing specialists via non-LinkedIn hacks; high-tech companies finding C++ developers via developer forums"
      ]
    },
    socialProof: {
      testimonial: "\"Finally understood why we're losing seniors to remote US roles – changed everything.\"",
      author: "Recruiter",
      company: "CZ based tech firm (2025)",
      roiData: ""
    },
    ctaText: "DOWNLOAD NOW – Unlock Your Full Market Insights Hacker Kit (Czech + US Checklists)"
  },
  upsells: [
    {
      type: "advanced" as const,
      title: "Want to see what Advanced Module 1 actually unlocks?",
      description: "Most recruiters stop here and keep losing talent. The few who dominate upgrade to the Advanced version – where Module 1 becomes a full predictive market report with AI-driven shortage forecasts, expat hiring playbooks, and custom salary models.",
      ctaText: "Sign Up for Waiting List",
      ctaLink: "#",
      features: [
        "Expanded 2025–2030 forecasts (e.g., AI job growth details, graduate gaps)",
        "Deeper regional comparisons with competitor benchmarking",
        "Predictive tools for talent migration (e.g., who's leaving CZ for US remote)",
        "Advanced creative sourcing maps (offline events, hidden communities)",
        "Personalised market audit of your company's positioning"
      ]
    },
    {
      type: "consulting" as const,
      title: "Still taking it in?",
      description: "Book a free 30-minute audit. I'll look at your current hiring challenges through the 2025 lens and show you – stat by stat – where Advanced changes the game.",
      ctaText: "Book Free 30-Min Audit – Understand Your Market Like Never Before",
      ctaLink: "https://calendly.com/_abaddion/30min"
    }
  ],
  quizQuestions: [
    {
      id: 1,
      question: "What's the CZ senior salary range for principals?",
      options: ["€40K–€60K", "€60K–€100K", "€100K–€150K", "€150K+"],
      correct: 1,
      explanation: "The CZ senior salary range for principals is €60K–€100K, with variations based on specialization and experience."
    },
    {
      id: 2,
      question: "What percentage of Czech seniors prefer hybrid/remote work?",
      options: ["40%", "60%", "70%", "90%"],
      correct: 2,
      explanation: "70% of Czech seniors prefer hybrid/remote work options, making flexibility a key motivator."
    },
    {
      id: 3,
      question: "What's the US total compensation range for principals?",
      options: ["$100K–$150K", "$150K–$250K", "$250K–$350K", "$350K+"],
      correct: 1,
      explanation: "The US total compensation range for principals is $150K–$250K, including base salary, bonuses, and equity."
    },
    {
      id: 4,
      question: "What's the CZ IT talent shortage percentage?",
      options: ["45%", "63%", "74%", "85%"],
      correct: 1,
      explanation: "The CZ IT talent shortage is 63%, creating significant challenges for companies seeking principal-level talent."
    },
    {
      id: 5,
      question: "What's the US IT talent shortage percentage?",
      options: ["63%", "68%", "74%", "80%"],
      correct: 2,
      explanation: "The US IT talent shortage is 74%, even higher than the Czech Republic, making principal talent extremely competitive."
    }
  ]
};

export const module2Config = {
  title: "Crafting Compelling Job Descriptions for Principal IT Roles",
  description: "In 2025's brutal market—63% senior shortage in CZ, 74% in US (Czech National AI Strategy 2030, BLS 2025)—your JD is the #1 tool to attract principals (10+ years, leadership in AI/ML, Cybersecurity, DevOps) without drowning in unqualified apps (100+ CZ or 200+ US per role). This module covers basics: turning lists into stories, incorporating trends (hybrid 2.0, ESG, mental health), and using competency models for authentic branding that cuts resume lies (72%).",
  duration: "30 min",
  sections: [
    {
      id: -1,
      title: "Before You Start: Your Secret Weapon Awaits",
      shortTitle: "Before Start",
      icon: Award,
      content: "beforeStart"
    },
    {
      id: 0,
      title: "Welcome to Defining Principal Talent",
      shortTitle: "Welcome",
      icon: BookOpen,
      content: "welcome"
    },
    {
      id: 1,
      title: "Characteristics of Principal Talent",
      shortTitle: "Characteristics",
      icon: Target,
      content: `
        <p style="margin-bottom: 2rem;" class="section-intro-text">Principal IT candidates are rare, high-impact professionals with 10+ years of experience, blending technical mastery with leadership. In 2025, they drive innovation in Czech and US tech firms, from startups to enterprise cloud companies.</p>
        
        <div class="challenge-cards challenge-cards-mobile-single" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <circle cx="12" cy="12" r="6"/>
                <path d="M12 2v4"/>
                <path d="M12 18v4"/>
                <path d="M2 12h4"/>
                <path d="M18 12h4"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Technical Mastery</h3>
              <p>Deep expertise in niche areas. In the Czech Republic, this includes kernel-level programming (e.g., for cybersecurity firms) or Python for AI models. In the US, it's advanced skills like TensorFlow for machine learning or Kubernetes for cloud architecture.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Leadership</h3>
              <p>They lead complex projects (e.g., cloud migrations) or mentor teams. Example: A principal cloud architect might design a scalable SaaS platform, guiding junior developers.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12,6 12,12 16,14"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Experience</h3>
              <p>10+ years in specialised roles, often with a track record of delivering high-stakes projects (e.g., AI systems for fraud detection).</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Strategic Impact</h3>
              <p>They solve big problems, like optimising data pipelines for SaaS companies (CZ) or securing fintech platforms for cloud enterprises (US).</p>
            </div>
          </div>
            </div>
        
        ${createCaseInsight(
          "Case Insight",
          "We sourced a Malware Analysts with 9+ years of experience who could reverse-engineer code at the kernel level. Their ability to lead threat analysis projects was key - both technical and strategic."
        )}
      `
    },
    {
      id: 2,
      title: "What Seniors Value",
      shortTitle: "Values",
      icon: Users,
      content: `
        <p style="margin-bottom: 2rem;" class="section-intro-text">Principal talent prioritises specific motivators, shaped by market dynamics in the Czech Republic and US:</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
                <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"/>
                <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Impactful Projects</h3>
              <p>Seniors want to shape cutting-edge work, like building AI platforms or cybersecurity frameworks. In CZ, 63% of firms face shortages for such roles, so highlighting impact (e.g., "Lead our AI transformation") is critical. In the US, with 74% of employers struggling, high-visibility projects (e.g., "Architect our cloud migration") attract talent.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Autonomy</h3>
              <p>They seek freedom to innovate, whether designing systems or choosing tools. CZ seniors value flexible decision-making; US seniors often expect equity (0.1–0.5%) to align with autonomy.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9,22 9,12 15,12 15,22"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Remote/Hybrid Options</h3>
              <p>In CZ, 60% of IT roles are hybrid/remote, driven by post-COVID shifts to US remote jobs ($150K–$250K vs. CZ €60K–€100K). In the US, 50% are in-office, 30% hybrid, 20% remote, so flexibility is a key perk.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Competitive Pay</h3>
              <p>CZ salaries range from €60K–€100K (e.g., €78K for AI engineers, €88K for blockchain devs). US salaries are $150K–$250K (e.g., $177K total for senior software engineers). Bonuses or learning budgets (€/$1K–$5K) sweeten offers.</p>
            </div>
          </div>
            </div>
        
        ${createCaseInsight(
          "Case Insight",
          "We secured a C++ developer by emphasizing autonomy over a real-time communication project and a competitive package, beating multinationals. Know what seniors value, and you'll win them over."
        )}
      `
    },
    {
      id: 3,
      title: "Common Roles in Demand",
      shortTitle: "Roles",
      icon: TrendingUp,
      content: `
        <p style="margin-bottom: 2rem;" class="common-roles-intro">Both markets face acute demand for principal-level roles, driven by 2025 tech trends:</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Czech Republic</h3>
              <p><strong>Cybersecurity Specialists:</strong> Needed to counter rising threats (63% shortage). Skills: Threat analysis, kernel-level programming.</p>
              <p><strong>AI/ML Engineers:</strong> High demand for generative AI (e.g., at AI-focused startups). Skills: Python, TensorFlow.</p>
              <p><strong>DevOps Engineers:</strong> Critical for cloud infrastructure (e.g., at travel tech companies). Skills: Kubernetes, CI/CD.</p>
              <p><strong>Data Engineers:</strong> Growing need for data pipelines. Skills: Apache Spark, SQL.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>United States</h3>
              <p><strong>AI/ML Specialists:</strong> 74% shortage for AI-driven projects (e.g., at data analytics companies). Skills: PyTorch, deep learning.</p>
              <p><strong>Cybersecurity Analysts:</strong> Essential for fintech and data security. Skills: Penetration testing, SIEM tools.</p>
              <p><strong>Cloud Architects:</strong> High demand for enterprise migrations (e.g., at cloud data platforms). Skills: AWS, Azure.</p>
              <p><strong>Data Scientists:</strong> Needed for analytics (26.5% CAGR in data market). Skills: R, big data frameworks.</p>
              <div style="margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid #e5e7eb; font-size: 0.9rem; color: #6b7280; line-height: 1.6;">
                <p style="margin: 0 0 0.5rem 0;"><strong>Footnote:</strong> CAGR stands for Compound Annual Growth Rate. This is a key metric in business and market analysis, representing the smoothed annualised growth rate of a value (like market size, revenue, or demand) over a specified period, assuming compounding effects.</p>
                <p style="margin: 0 0 0.5rem 0;">In the context of "26.5% CAGR in data market," it means the data analytics sector is projected to grow at an average rate of 26.5% per year, compounded, highlighting massive demand for roles like Data Scientists. As a recruiter who's sourcing top talent for high-growth tech firms, you should see this as a signal for TA teams to ramp up proactive sourcing in analytics—skills in Python, SQL, and ML frameworks are gold right now. If you're building a pipeline, this is your focus for 2026!</p>
              </div>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
                <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"/>
                <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Market Insight</h3>
              <p>CZ's smaller market (15,000 new jobs) focuses on cost-effective innovation; US's larger market (317,700 openings) emphasizes scale and AI adoption. Tailor roles to local needs.</p>
            </div>
          </div>
        </div>
        
        ${createCaseInsight(
          "Explanation",
          `<strong>How to translate these 2025 data into 2026 planning?</strong><br /><br />
It requires adjusting for escalating trends like AI disruptions and persistent shortages—drawing from fresh projections (e.g., Europe's 57-76% tech firm shortages in CZ, US >90% IT skills gaps by 2026, and data analytics CAGR forecasts holding at 27-30% through 2030).<br /><br />
To translate these figures, start by inflating shortages modestly (e.g., CZ's 63% could rise to 65-70% amid 2.3M workers needing AI retraining; US's 74% to 80-85% with $5.5T global productivity losses), maintain job openings at similar scales (CZ ~15,000 new roles focusing on efficiency; US 317,700+ stabilising post-900K 2025 additions but growing 7-8% in tech), and extend the 26.5% data CAGR to 27-30% based on market expansions to $130-300B by 2026—prioritising roles like AI/ML Engineers and Cybersecurity Specialists that show 15-25% demand spikes.<br /><br />
<strong>Preparation hacks</strong>: Audit your pipeline now using our Free Version Module 1 for basic stats translation, upgrade to Advanced for detailed A/B testing on 2026-adjusted Boolean strings (boosting leads 35%), or join the Masterclass for AI-predictive modeling to forecast gaps 6 months out, saving €100K in mis-hires.<br /><br />
<strong>Pro tip</strong>: Hack into niche communities like Prague Devs or IEEE events for hidden talents—we've cut time-to-hire to <45 days this way; and/or book a free 30-min audit to tailor your 2026 strategy toward my consulting for elite scaling.`
        )}
      `
    },
    {
      id: 4,
      title: "Interactive Task (you need the PDF)",
      shortTitle: "Task",
      icon: FileText,
      content: "activity"
    },
    {
      id: 5,
      title: "Quiz – 5 questions taken verbatim from the kit.",
      shortTitle: "Quiz",
      icon: Trophy,
      content: "quiz"
    },
    {
      id: 6,
      title: "Takeaway: Your Principal JD Hacker Kit",
      shortTitle: "Takeaways",
      icon: Download,
      content: "takeaway"
    }
  ],
  resources: [
    {
      title: "Principal IT Role Job Description Template",
      type: "pdf" as const,
      url: "principal-job-description-template.pdf",
      downloadable: true
    }
  ],
  pdfTeaser: {
    title: "Before You Start",
    subtitle: "The JD is the make-or-break moment in principal hiring.",
    description: "The 41-page Principal JD Hacker Kit you're about to download is the exact framework I used with cybersecurity firms (5 specialist hires in 45 days when competitors failed) and with high-tech companies (real-time systems roles closed in weeks). Without it you keep getting generic applications from juniors. With it you start writing JDs that make some passive seniors reach out first. Even if they don't, once you reach out to them and present the role, they will appreciate the professionalism, which is very crucial at the early stage of the conversation.",
    urgency: {
      text: "Required for the activities.",
      bonus: "Limited-time bonus: download within 24 hours and get a free 15-minute live JD review with me (normally reserved for private clients)."
    },
    teasers: {
      title: "Exact preview – taken word-for-word from your PDF:",
      items: [
        "Intake Meeting Form (pages 6–11) – every field from Title, Level, Location, Must-Haves/Nice-to-Haves, Compensation, Success Metrics, \"Not a Good Fit\" red flags",
        "Hiring Manager & Team Questionnaires (pages 13–18) – the questions that create authentic culture copy",
        "8 Key Points Checklist (pages 28–31) – the exact framework that stops 72% of resume lies",
        "3 complete CZ/US JD examples (Cybersecurity Architect for AI startups, Principal DevOps for cloud platforms, Principal AI/ML for travel tech companies – pages 24–27)",
        "25–60 minute hands-on activities that turn intake data into story-based JDs",
        "Full 2025 trends: hybrid work 2.0, mental health support, ESG, radical openness, no micro-management"
      ]
    },
    socialProof: {
      testimonial: "\"20–25% more senior applications in the first month – finally a JD that works.\"",
      author: "Recruiter",
      company: "European tech company (2025)",
      roiData: ""
    },
    ctaText: "DOWNLOAD NOW – Unlock the Full 41-Page Principal JD Hacker Kit"
  },
  upsells: [
    {
      type: "advanced" as const,
      title: "Want to see what Advanced Module 2 actually gives you?",
      description: "Most recruiters stop here and stay stuck with average results. The few who level-up go to the Advanced version and start closing principals in <60 days while others are still screening hundreds of juniors.",
      ctaText: "Sign Up for Waiting List",
      ctaLink: "#",
      features: [
        "A/B testing framework that delivers 35–40% more senior applications (real client results)",
        "10+ additional JD examples with full before/after metrics",
        "Deeper competency-model weighting (40% skills + leadership + culture = dramatically better long-term hires)",
        "Stakeholder scripts & email templates that get even the busiest engineering leads to fill the intake form in one meeting",
        "AI prompts I use with private clients to cut JD writing time by 80%",
        "Personalised markup of your own JDs (submit → I review line-by-line and send back comments)"
      ]
    },
    {
      type: "consulting" as const,
      title: "Still deciding?",
      description: "Book a free 30-minute audit. I'll take one of your current JDs (or the one you just built) and show you – line by line – exactly where the Advanced playbook changes everything.",
      ctaText: "Book Free 30-Min Audit – Make Your Next JD Unstoppable",
      ctaLink: "https://calendly.com/_abaddion/30min"
    }
  ],
  quizQuestions: [
    {
      id: 1,
      question: "What defines a principal IT candidate's experience level?",
      options: ["2–5 years", "5–10 years", "10+ years", "No experience"],
      correct: 2,
      explanation: "Principal roles require 10+ years of experience with deep technical mastery and leadership capabilities."
    },
    {
      id: 2,
      question: "What is a top motivator for CZ principal talent?",
      options: ["Low salary", "Remote/hybrid options", "In-office mandates", "Short-term contracts"],
      correct: 1,
      explanation: "Czech principals often prioritise remote/hybrid work options, especially post-COVID when they can earn more in remote US/EU roles."
    },
    {
      id: 3,
      question: "Which role is in high demand in both CZ and US markets?",
      options: ["Graphic Designer", "AI/ML Engineer", "Office Manager", "Junior Tester"],
      correct: 1,
      explanation: "AI/ML Engineers are in high demand in both markets due to the AI boom and need for specialized technical expertise."
    },
    {
      id: 4,
      question: "What's the average salary for a CZ principal AI engineer?",
      options: ["€20,000–€40,000", "€60,000–€100,000", "€150,000–€200,000", "€10,000–€20,000"],
      correct: 1,
      explanation: "CZ principal AI engineers typically earn €60,000–€100,000, significantly less than US remote roles."
    },
    {
      id: 5,
      question: "What's a key leadership trait of principal talent?",
      options: ["Writing basic code", "Leading complex projects", "Managing office supplies", "Attending meetings only"],
      correct: 1,
      explanation: "Principal talent leads complex projects, mentors teams, and makes strategic technical decisions."
    }
  ]
};

export const module3Config = {
  title: "Sourcing Strategies for Principal IT Talent",
  description: "Module 3: Sourcing Strategies for Principal IT Talent - 90% of principals are passive—they're not applying, but open. This module teaches basics to uncover them: Boolean templates, GitHub outreach, communities, referrals, AI starters.",
  duration: "45 min",
  sections: [
    {
      id: -1,
      title: "Before You Start",
      shortTitle: "Before Start",
      icon: Award,
      content: "beforeStart"
    },
    {
      id: 0,
      title: "Welcome to Sourcing Strategies",
      shortTitle: "Welcome",
      icon: BookOpen,
      content: "welcome"
    },
    {
      id: 1,
      title: "LinkedIn Recruiter: Boolean Searches",
      shortTitle: "LinkedIn",
      icon: Target,
      content: `
        <p style="margin-bottom: 2rem;">LinkedIn Recruiter is powerful for sourcing seniors, but only with precise Boolean searches to filter the noise (e.g., 100+ applicants per CZ role, 200+ in the US).</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p>Use targeted queries like: <strong>"senior AND (developer OR engineer) AND (AI OR 'machine learning') AND Prague -junior"</strong>. Filter by 10+ years experience and skills (e.g., TensorFlow, Kubernetes).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p>Try: <strong>"senior AND (developer OR engineer) AND (AI OR cybersecurity) AND (San Francisco OR remote) -junior"</strong>. Add locations like Seattle or Austin for tech hubs.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Pro Tips</h4>
            </div>
            <p>Use quotes for phrases (e.g., "cloud architect"), exclude irrelevant terms (e.g., -intern), and check profiles for recent activity (e.g., posts on AI trends).</p>
          </div>
        </div>
        
        ${createCaseInsight(
          "Case Insight: CZ Startup Success",
          "For a CZ startup, I used <strong>\"senior AND devops AND Kubernetes AND Brno\"</strong> to find a principal engineer in 30 days, bypassing unqualified applicants."
        )}
      `
    },
    {
      id: 2,
      title: "GitHub: Engaging Contributors",
      shortTitle: "GitHub",
      icon: Users,
      content: `
        <p style="margin-bottom: 2rem;">GitHub is a goldmine for passive seniors contributing to open-source projects.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p>Search repos for CZ-relevant skills (e.g., Python for AI at AI-focused startups). Check contributor profiles for Prague/Brno locations and email them via public contacts.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p>Target repos for US-demand skills (e.g., TensorFlow for AI at data analytics companies). Look for contributors in tech hubs or remote-friendly profiles.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <rect width="20" height="16" x="2" y="4" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
              </div>
              <h4>Engagement Strategy</h4>
            </div>
            <p>Send personalised emails (e.g., "Hi [Name], your work on [repo] aligns with our AI project in Prague…"). Keep it concise, focus on project impact.</p>
          </div>
        </div>
        
        ${createCaseInsight(
          "Case Insight: Cybersecurity Success",
          "For a cybersecurity firm, I found a low-level developer by emailing a contributor to a cybersecurity repo, leading to a hire others missed."
        )}
      `
    },
    {
      id: 3,
      title: "Tech Communities: Networking",
      shortTitle: "Networking",
      icon: TrendingUp,
      content: `
        <p style="margin-bottom: 2rem;">Local tech communities connect you with passive seniors.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p>Attend Prague Devs or Brno Tech Talks meetups (weekly events, 50–200 attendees). Network at Czech ICT Summit (annual, 1,000+ professionals). Offer to speak on AI hiring to attract candidates.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p>Join TechCrunch Disrupt (San Francisco, 5,000+ attendees) or local meetups like SF Bay Area Devs. Sponsor hackathons to engage seniors (e.g., AWS Re:Invent).</p>
          </div>
          
            <div class="challenge-card">
              <div class="challenge-card-header">
                <div class="challenge-icon">
                  ${ORANGE_LIGHTBULB_ICON}
                </div>
                <h4>Networking Tips</h4>
            </div>
            <p>Bring business cards, discuss niche topics (e.g., Kubernetes for devops), and follow up via LinkedIn. In CZ, emphasise work-life balance; in US, highlight equity.</p>
          </div>
        </div>
        
        ${createCaseInsight(
          "Case Insight: Prague Meetup Success",
          "For a client, I met a C++ developer at a Prague tech meetup, securing a hire by discussing real-time systems - proof communities beat job boards."
        )}
      `
    },
    {
      id: 4,
      title: "Referrals: Building Programs",
      shortTitle: "Referrals",
      icon: FileText,
      content: `
        <p style="margin-bottom: 2rem;">Referral programs tap employee networks for senior talent.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p>Offer €2,000 bonuses for principal referrals (e.g., AI engineers). Train staff to identify seniors at meetups or in CZ tech groups (e.g., Prague Devs Slack).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p>Provide $5,000 bonuses for referrals (e.g., cloud architects). Leverage networks like IEEE or ACM, training employees to spot talent.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Program Tips</h4>
            </div>
            <p>Promote internally via Slack or email. Highlight success stories (e.g., "Our devops lead referred a principal, saving €20K in fees").</p>
          </div>
        </div>
        
        ${createCaseInsight(
          "Case Insight: Gaming Client Success",
          "For a gaming client, I built a referral program that sourced a senior devops engineer in 3 weeks, cutting agency costs."
        )}
      `
    },
    {
      id: 5,
      title: "AI Tools: Automating Sourcing",
      shortTitle: "AI Tools",
      icon: Trophy,
      content: `
        <p style="margin-bottom: 2rem;">AI tools streamline sourcing, saving 80% of screening time.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p>Use Paradox for resume screening (matches skills like Python) or Hunter.io for outreach emails. Integrate with Workable ATS for CZ startups.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p>Implement HireVue or Gloat for screening (e.g., Kubernetes skills) and Gem for automated emails. Pair with Lever ATS for US tech firms.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>AI Best Practices</h4>
            </div>
            <p>Set filters for 10+ years experience and niche skills. Test AI outputs to ensure accuracy (e.g., no false positives for AI roles).</p>
          </div>
        </div>
        
        ${createCaseInsight(
          "Case Study: CZ Startup Success",
          "For a CZ startup, I used Textkernel to screen 200 applicants, identifying a principal AI engineer in 45 days, saving €15K vs. agency fees. The system now runs autonomously."
        )}
      `
    },
    {
      id: 6,
      title: "Hands-On Sourcing Practice",
      shortTitle: "Activity",
      icon: MessageSquare,
      content: "activity"
    },
    {
      id: 7,
      title: "Test Your Sourcing Knowledge",
      shortTitle: "Quiz",
      icon: Trophy,
      content: "quiz"
    },
    {
      id: 8,
      title: "Takeaway: Your Sourcing Hacker Kit",
      shortTitle: "Takeaway",
      icon: Download,
      content: "takeaway"
    }
  ],
  resources: [
    {
      title: "Sourcing Hacker Kit (Cheat Sheet Basics)",
      type: "pdf" as const,
      url: "sourcing-hacker-kit-cheat-sheet.pdf",
      downloadable: true
    }
  ],
  pdfTeaser: {
    title: "Before You Start",
    subtitle: "Job boards fail for principals.",
    description: "The Sourcing Hacker Kit (cheat sheet) you're about to download is the exact basics I used with cybersecurity firms (specialist analysts via forums/universities—no LinkedIn profiles) and high-tech companies (C++ via niche communities in weeks). Without it → generic searches, low responses. With it → copy-paste templates and tips to start sourcing hidden talent today.",
    urgency: {
      text: "Required for the 45-min activity + quiz.",
      bonus: "Limited-time bonus: download within 24 hours and get a free 15-minute sourcing review with me (normally reserved for private clients)."
    },
    teasers: {
      title: "Exact preview – taken word-for-word from your Free PDF (no hype, only what's really inside):",
      items: [
        "Prep for Sourcing (30-min activity): Target Candidate Profile (skills/motivators), Channel Selection Guide (CZ: Jobs.cz/LinkedIn/GitHub/Prague Devs; US: LinkedIn/StackOverflow/IEEE)",
        "Sourcing Strategies (45-min activity): Boolean templates (e.g., CZ: \"senior AND (AI OR cybersecurity OR devops) AND Prague -junior\"; US similar + remote), GitHub search examples",
        "Community Tips: CZ (Prague Devs, Czech ICT Summit); US (TechCrunch Disrupt, IEEE)",
        "Best Practices (15-min checklist): Define profile, personalised outreach, track responses (>20% target), CZ/US tips (hybrid/learning CZ, equity/projects US)",
        "Examples: cybersecurity firm forum sourcing, high-tech C++ via communities"
      ]
    },
    socialProof: {
      testimonial: "\"20%+ responses from my first week—the Boolean templates + tips worked immediately.\"",
      author: "Recruiter",
      company: "European tech company (2025)",
      roiData: ""
    },
    ctaText: "DOWNLOAD NOW – Unlock Your Sourcing Hacker Kit (Cheat Sheet Basics)"
  },
  upsells: [
    {
      type: "advanced" as const,
      title: "Want to see what Advanced Module 3 actually unlocks?",
      description: "Most recruiters stop here and get sporadic results. The few who dominate upgrade to the Advanced version – where Module 3 is multi-page with candidate profile forms, tool setup guides, Boolean mastery (20+ examples/hacks), GitHub deep dive (GraphQL API tricks 47% more candidates—Beehiiv 2025), Stack Overflow/X.com strategies, full referral design, AI hacks (217% more leads—HireEZ 2025), and hidden hacks section.",
      ctaText: "Sign up for Advanced Waiting List",
      ctaLink: "#",
      features: [
        "Candidate Profile Form + Tool Setup Guide (30/20-min activities for precision)",
        "Boolean Mastery: 20+ examples + hacks (wildcards, X-ray for 25–34% more profiles—Evaboot/LinkedIn 2025)",
        "GitHub Deep Dive: Step-by-step + GraphQL queries (47% better quality—Beehiiv 2025)",
        "Stack Overflow + X.com Hacks: Reputation filters, hashtags for 25–30% engagement",
        "Full Community + Referral Playbooks: Templates, tiered rewards (3x ROI—Viral Loops 2025)",
        "AI Sourcing Hacks: Custom models, implementation (217% leads, 41% cost reduction—HireEZ 2025)",
        "Expanded Checklist + Hidden Hacks: Research-backed tricks for CZ/US"
      ]
    },
    {
      type: "consulting" as const,
      title: "Still building confidence?",
      description: "Book a free 30-minute audit. I'll review one of your searches (or from the Free kit) and show you – hack by hack – exactly where Advanced changes everything.",
      ctaText: "BOOK FREE 30-MIN AUDIT – UNCOVER HIDDEN TALENT LIKE A PRO",
      ctaLink: "https://calendly.com/_abaddion/30min"
    }
  ],
  quizQuestions: [
    {
      id: 0,
      question: "What's a key platform for sourcing passive seniors?",
      options: ["Facebook", "GitHub", "Instagram", "TikTok"],
      correct: 1,
      explanation: "GitHub is a goldmine for passive seniors contributing to open-source projects."
    },
    {
      id: 1,
      question: "Which Boolean search targets CZ senior AI engineers?",
      options: [
        "\"junior AND AI AND Prague\"",
        "\"senior AND (developer OR engineer) AND AI AND Prague -junior\"",
        "\"AI AND intern\"",
        "\"developer AND Brno\""
      ],
      correct: 1,
      explanation: "The correct search uses 'senior' with proper Boolean operators and excludes 'junior' to target experienced AI engineers in Prague."
    },
    {
      id: 2,
      question: "What's a top CZ tech community for networking?",
      options: ["Prague Devs", "Comic-Con", "Food Festival", "Fashion Week"],
      correct: 0,
      explanation: "Prague Devs is a key tech community for networking with senior IT professionals in the Czech Republic."
    },
    {
      id: 3,
      question: "What's the referral bonus for a CZ principal hire?",
      options: ["€500", "€2,000", "€10,000", "€100"],
      correct: 1,
      explanation: "CZ companies typically offer €2,000 bonuses for principal referrals to incentivize employee networks."
    },
    {
      id: 4,
      question: "Which AI tool saves 80% of screening time?",
      options: ["Photoshop", "Paradox", "Excel", "Canva"],
      correct: 1,
      explanation: "Paradox is an AI tool that streamlines resume screening and saves significant time in the sourcing process."
    }
  ]
};

export const module4Config = {
  title: "Employer Branding for Senior Talent",
  description: "Module 4: Employer Branding for Senior Talent - Seniors choose brands showing real tech stacks, impact, autonomy—not generic \"great culture\". This module teaches basics to attract principals in CZ/US: optimise pages/platforms, create content, engage events.",
  duration: "45 min",
  sections: [
    {
      id: -1,
      title: "Before You Start",
      shortTitle: "Before Start",
      icon: Award,
      content: "beforeStart"
    },
    {
      id: 0,
      title: "Welcome to Employer Branding for Senior Talent",
      shortTitle: "Welcome",
      icon: BookOpen,
      content: "welcome"
    },
    {
      id: 1,
      title: "Key Elements of a Senior-Friendly Employer Brand",
      shortTitle: "Key Elements",
      icon: Target,
      content: `
        <p style="margin-bottom: 2rem;">To attract principal candidates, your employer brand must highlight what they value: cutting-edge tech, impactful projects, and a culture that supports autonomy and flexibility.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Tech Stack</h3>
              <p>Showcase modern tools (e.g., React, AWS, TensorFlow) on career pages. In CZ, tech companies' redesigns highlighting Go and Kubernetes boosted senior applications by 20%. In the US, data analytics companies' focus on Python and Spark attracts AI specialists.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12,6 12,12 16,14"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Project Impact</h3>
              <p>Emphasize high-stakes work (e.g., "Build our payment platform" or "Lead our AI fraud detection"). Seniors in CZ want meaningful roles; US candidates seek enterprise-scale projects.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Culture</h3>
              <p>Promote autonomy (e.g., freedom to choose tools) and flexibility. CZ seniors prefer 60% hybrid/remote work; US seniors value 30% hybrid, 20% remote options, or equity (0.1–0.5%).</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Testimonials</h3>
              <p>Feature senior engineers' stories (e.g., "How I architected our cloud migration"). This builds trust, especially for CZ startups competing with multinationals like JetBrains.</p>
            </div>
          </div>
        </div>
        
        ${createCaseInsight(
          "Case Insight",
          "For a Brno SaaS firm, I advised adding tech stack details and remote work information to their career page, increasing senior applicants by 15% in 2024."
        )}
      `
    },
    {
      id: 2,
      title: "Optimise Platforms for Visibility",
      shortTitle: "Optimise",
      icon: Globe,
      content: `
        <p style="margin-bottom: 2rem;">Your career page and job platforms must reflect senior priorities to stand out in competitive markets.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p>Update Jobs.cz or any other job board (like startupjobs.cz), though these platforms don't really work effectively for principals. Focus on optimising your career page and LinkedIn profiles to highlight senior-led projects (e.g., "Our AI pipeline at our AI startup"). Emphasize 60% hybrid/remote options, as 70% of CZ seniors prefer flexibility. Include salary ranges (€60K–€100K) for transparency.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p>Enhance LinkedIn and Glassdoor profiles with project impact (e.g., "Lead cloud migrations at data platforms") and equity offers (0.2–0.5%). Highlight hybrid (30%) or remote (20%) options to compete with Big Tech companies.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Tips</h4>
            </div>
            <p>Use visuals (e.g., team photos, tech stack diagrams) and clear CTAs (e.g., "Apply to lead our AI projects"). Refresh content quarterly to stay relevant.</p>
          </div>
          
        </div>
        
        ${createCaseInsight(
          "Example",
          "Productboard's LinkedIn overhaul in 2024, showcasing Python and data engineering projects, drew 25% more principal applicants."
        )}
      `
    },
    {
      id: 3,
      title: "Engage via Content",
      shortTitle: "Content",
      icon: PenTool,
      content: `
        <p style="margin-bottom: 2rem;">Content builds credibility and attracts passive seniors.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Tech Blogs</h3>
              <p>Publish posts like "How We Built Our AI Pipeline" on your site or Medium. In CZ, focus on work-life balance (e.g., 4-day weeks at travel tech companies); in the US, highlight innovation (e.g., data analytics companies' ML breakthroughs).</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Open-Source Contributions</h3>
              <p>Contribute to GitHub repos (e.g., TensorFlow for AI roles). Showcase your team's commits to attract seniors.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Speaking Engagements</h3>
              <p>Speak at Prague Tech Fest (CZ, 500+ attendees) or TechCrunch Disrupt (US, 5,000+ attendees). Share insights on AI hiring to connect with principals.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Tips</h4>
            </div>
            <p>Promote content on LinkedIn and X, tagging relevant communities (e.g., Prague Devs, IEEE). In CZ, emphasize local impact; in US, global scale.</p>
          </div>
        </div>
        
        ${createCaseInsight(
          "Case Insight",
          "For a DevOps-focused company, I helped publish a blog on their devops pipeline, shared at Brno Tech Talks, attracting a principal engineer within 6 weeks."
        )}
      `
    },
    {
      id: 4,
      title: "Hands-On Activity (you need the PDF)",
      shortTitle: "Activity",
      icon: Search,
      content: "activity"
    },
    {
      id: 5,
      title: "Quiz: Test Your Employer Branding Knowledge",
      shortTitle: "Quiz",
      icon: Trophy,
      content: "quiz"
    },
    {
      id: 6,
      title: "Takeaway: Your Branding Hacker Kit",
      shortTitle: "Takeaway",
      icon: Download,
      content: "takeaway"
    }
  ],
  resources: [
    {
      title: "Branding Hacker Kit (Checklist)",
      type: "pdf" as const,
      url: "branding-hacker-kit-checklist.pdf",
      downloadable: true
    }
  ],
  pdfTeaser: {
    title: "Before You Start",
    subtitle: "Weak branding = invisible to passives.",
    description: "The Branding Hacker Kit (checklist) you're about to download is the exact basics I used with cybersecurity firms (tech-focused pages boosting seniors) and high-tech companies (impact stories drawing real-time experts). Without it → bland pages, low apps. With it → audit/fix your brand for immediate senior pull.",
    urgency: {
      text: "Required for the audit activity + quiz.",
      bonus: "Limited-time bonus: download within 24 hours and get a free 15-minute branding review with me (normally reserved for private clients)."
    },
    teasers: {
      title: "Exact preview – taken word-for-word from your Free PDF (no hype, only what's really inside):",
      items: [
        "Prep for Branding (30-min activity): Brand Audit Form (perception, feedback, gaps, channels), Stakeholder Questions (marketing/leadership/employees/HR)",
        "Branding Strategies (25-30 min activity): Senior tips (tech stacks like React/AWS/TensorFlow, impact, culture/autonomy, CZ hybrid/learning, US equity/projects), Content Guide (career pages for CZ e-commerce companies or US data analytics platforms, tech blogs on CzechCrunch/TechCrunch, events)",
        "Best Practices (15-min checklist): Authenticity/ESG/benefits/tech channels/communities/innovation, avoid overpromising (72% lies), CZ/US tips (hybrid/Prague scene, equity/enterprise)",
        "Examples: Brno SaaS-inspired hybrid focus (25% time-to-hire cut)"
      ]
    },
    socialProof: {
      testimonial: "\"20%+ more senior apps after simple fixes—the audit form was eye-opening.\"",
      author: "Recruiter",
      company: "European tech company (2025)",
      roiData: ""
    },
    ctaText: "DOWNLOAD NOW – Unlock Your Branding Hacker Kit"
  },
  upsells: [
    {
      type: "advanced" as const,
      title: "Want to see what Advanced Module 4 actually unlocks?",
      description: "Most recruiters stop here and stay average. The few who dominate upgrade to the Advanced version – where Module 4 is multi-page with scoring frameworks, detailed theory (senior psychology, components, platforms, content), 3 practical tasks (audit/plan, content creation, optimisation), A/B testing guides, and full stakeholder templates for 35–40% more apps.",
      ctaText: "Sign up for Advanced Waiting List",
      ctaLink: "#",
      features: [
        "Brand Audit Form with 1–5 scoring + competitor benchmarks",
        "Stakeholder Questions + analysis tips",
        "Theory: Senior psychology (motivators vs. juniors), components (tech stack diagrams, impact templates, autonomy messaging), platforms (A/B testing career pages), content (blog outlines, open-source showcase)",
        "3 Tasks: 90-day plan, content draft, metrics tracking",
        "Expanded Checklist + CZ/US hacks (e.g., sponsorship ROI)"
      ]
    },
    {
      type: "consulting" as const,
      title: "Still auditing your brand?",
      description: "Book a free 30-minute audit. I'll review your career page (or from the Free kit) and show you – element by element – exactly where Advanced changes everything.",
      ctaText: "BOOK FREE 30-MIN AUDIT – TURN YOUR BRAND INTO A SENIOR MAGNET",
      ctaLink: "https://calendly.com/_abaddion/30min"
    }
  ],
  quizQuestions: [
    {
      id: 1,
      question: "What's a key element of a senior-friendly employer brand?",
      options: ["Free snacks", "Tech stack visibility", "Dress code", "Office decor"],
      correct: 1,
      explanation: "Tech stack visibility is crucial. Showcasing modern tools (e.g., React, AWS, TensorFlow) on career pages helps attract principal candidates by highlighting the technical environment."
    },
    {
      id: 2,
      question: "What percentage of CZ seniors prefer hybrid/remote work?",
      options: ["20%", "70%", "10%", "50%"],
      correct: 1,
      explanation: "70% of Czech seniors prefer hybrid/remote work options, making flexibility a key motivator in the CZ market."
    },
    {
      id: 3,
      question: "What should CZ firms prioritise to attract senior talent?",
      options: ["Job boards like Jobs.cz", "Career page optimisation", "Social media ads", "Email campaigns"],
      correct: 1,
      explanation: "Career page optimisation is crucial for attracting principals. Job boards like Jobs.cz don't effectively reach senior talent, so focus on showcasing tech stacks, project impact, and flexibility options on your career page and LinkedIn profiles."
    },
    {
      id: 4,
      question: "What's a key US senior motivator?",
      options: ["Low salary", "Equity (0.1–0.5%)", "Fixed hours", "No remote work"],
      correct: 1,
      explanation: "Equity offers (0.1–0.5%) are a key motivator for US senior talent, along with hybrid (30%) or remote (20%) options to compete with Big Tech."
    },
    {
      id: 5,
      question: "How do tech companies' career page redesigns impact senior applications?",
      options: ["No change", "20% increase", "50% decrease", "10% increase"],
      correct: 1,
      explanation: "Tech companies' redesigns highlighting Go and Kubernetes on their career pages boosted senior applications by 20%, demonstrating the impact of tech stack visibility."
    }
  ]
};

export const module5Config = {
  title: "Screening and Engaging Senior Candidates",
  description: "Module 5: Screening and Engaging Senior Candidates - Seniors ghost generic processes—they want personalised outreach referencing their work and flexible, impact-focused screening. This module teaches basics to engage + evaluate principals in CZ/US: templates (LinkedIn/GitHub/referral), rubric (40% skills/30% experience/30% leadership-culture), tools (Codility CZ/HackerRank US/GitHub), avoid pitfalls. Hit >20% responses, 90% pass rates.",
  duration: "60 min",
  sections: [
    {
      id: -1,
      title: "Before You Start",
      shortTitle: "Before Start",
      icon: Award,
      content: "beforeStart"
    },
    {
      id: 0,
      title: "Welcome to Screening and Engaging Senior Candidates",
      shortTitle: "Welcome",
      icon: BookOpen,
      content: "welcome"
    },
    {
      id: 1,
      title: "Screening: Focus on Impact and Fit",
      shortTitle: "Screening",
      icon: Target,
      content: `
        <p style="margin-bottom: 2rem;">Effective screening identifies principal candidates with the right skills, experience, and cultural fit, avoiding over-reliance on automated systems.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p><strong>Technical Assessments:</strong> I don't recommend standard technical assessments—they're not best practice for principals. However, if you insist on using one or the hiring manager requires it, use Codility for coding tests (e.g., Python for AI engineers) or tailored challenges (e.g., cybersecurity scenarios for architects). Focus on real-world problems (e.g., "Optimise a payment pipeline").</p>
            <p><strong>Portfolio Impact:</strong> Review GitHub repos and past projects. Check for contributions to open-source AI tools common in the Czech market.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p><strong>Technical Assessments:</strong> I don't recommend standard technical assessments—they're not best practice for principals. However, if you insist on using one or the hiring manager requires it, leverage HackerRank for coding (e.g., TensorFlow for AI roles) or custom assessments (e.g., Kubernetes deployment for cloud architects). Prioritise enterprise-scale scenarios.</p>
            <p><strong>Portfolio Impact:</strong> Review GitHub repos, past projects, or patents. Look for impactful projects (e.g., cloud migrations at major companies).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Balanced Approach</h3>
              <p>Weight skills (40%), experience (30%), and leadership/culture (30%). Assess leadership via project outcomes (e.g., "Led a team to reduce latency by 20%").</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Case Insight</h3>
              <p>At a fintech client, we secured a principal fintech hire by designing a blockchain-specific assessment, avoiding generic coding tests that missed their expertise. Tailored screening closed the deal.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 2,
      title: "Engagement: Personalise and Be Flexible",
      shortTitle: "Engagement",
      icon: Mail,
      content: `
        <p style="margin-bottom: 2rem;">Engaging seniors requires personalised outreach and accommodating their schedules.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p><strong>Personalised Outreach:</strong> Craft emails like: "Hi [Name], your Python repo on GitHub aligns with our AI project at our tech company. Let's discuss a lead role with hybrid options." Emphasize work-life balance (70% prefer flexibility).</p>
            <p><strong>Flexible Interviews:</strong> Offer evening or virtual interviews via Zoom. Accommodate seniors balancing family (e.g., 6 PM slots).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p><strong>Personalised Outreach:</strong> Use: "Hi [Name], your TensorFlow project on GitHub fits our AI role at [company]. Interested in a principal position with 0.2% equity?" Highlight innovation and growth.</p>
            <p><strong>Flexible Interviews:</strong> Offer virtual interviews via Zoom. Provide weekend options for busy principals.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Tips</h4>
            </div>
            <p>Reference specific work (e.g., GitHub commits), keep emails under 100 words, and propose a 15-minute call. Follow up within 48 hours.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Case Insight</h3>
              <p>For a high-tech company, we engaged a C++ developer with a tailored email about their real-time systems repo, offering a virtual interview at 7 PM. They signed in 2 weeks.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 3,
      title: "Avoid Pitfalls",
      shortTitle: "Pitfalls",
      icon: AlertTriangle,
      content: `
        <p style="margin-bottom: 2rem;">Common mistakes derail senior hiring in competitive markets.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Over-Reliance on ATS</h3>
              <p>In CZ, 100+ applicants per role often clog ATS filters; in the US, 200+. Seniors bypass generic postings, so manually review GitHub/LinkedIn profiles.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Skills-Only Focus</h3>
              <p>Don't prioritise coding over leadership. Assess cultural fit (e.g., CZ's value for collaboration, US's focus on innovation) and mentoring ability.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Rigid Processes</h3>
              <p>Avoid 9–5 interviews or lengthy ATS forms. Seniors in CZ and US value efficiency and flexibility.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Generic Messaging</h3>
              <p>Vague emails (e.g., "Join our team!") fail. Reference specific projects or skills to stand out.</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Insight</h3>
              <p>For a CZ startup, we bypassed ATS filters to manually screen a principal devops engineer's Kubernetes portfolio, securing a hire others missed.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 4,
      title: "Activity: Role-Play Screening and Engagement",
      shortTitle: "Activity",
      icon: MessageSquare,
      content: "activity"
    },
    {
      id: 5,
      title: "Quiz: Test Your Screening and Engagement Knowledge",
      shortTitle: "Quiz",
      icon: Trophy,
      content: "quiz"
    },
    {
      id: 6,
      title: "Takeaway: Outreach and Screening Toolkit",
      shortTitle: "Takeaway",
      icon: Download,
      content: "takeaway"
    }
  ],
  resources: [
    {
      title: "Outreach & Screening Hacker Kit",
      type: "pdf" as const,
      url: "outreach-screening-hacker-kit.pdf",
      downloadable: true
    }
  ],
  pdfTeaser: {
    title: "Before You Start",
    subtitle: "Cold emails + rigid ATS lose 95% of seniors.",
    description: "The Outreach & Screening Hacker Kit (toolkit) you're about to download is the exact basics I used with fintech companies (assessments closing blockchain experts) and high-tech companies (personalised C++ engagement in weeks). Without it → low responses, mis-hires. With it → templates + rubric to start engaging/evaluating like a pro today.",
    urgency: {
      text: "Required for the role-play activity + quiz.",
      bonus: "Limited-time bonus: download within 24 hours and get a free 15-minute outreach/screening review with me (normally reserved for private clients)."
    },
    teasers: {
      title: "Exact preview – taken word-for-word from your Free PDF (no hype, only what's really inside):",
      items: [
        "Prep for Outreach/Screening (30-min activity): Candidate Persona Form (role/skills/motivators/culture/experience/communities), Screening Criteria Guide (technical/leadership/cultural/portfolio/red flags)",
        "Outreach/Screening Strategies (45-min activity): 3 Templates (CZ LinkedIn: \"Your TensorFlow fits our AI\"; US LinkedIn: \"Kubernetes repo for our migration\"; Referral), Tools (Codility CZ/HackerRank US/GitHub/AI like HireVue/Gloat), fintech company example",
        "Best Practices (15-min checklist): Personalise/reference projects, AI automate, assess fit (>80% scores), evaluate leadership, review portfolios over keywords, track metrics (>20% response/90% pass), avoid bias",
        "CZ/US Tips: Hybrid/€60K–€100K CZ, equity/enterprise US"
      ]
    },
    socialProof: {
      testimonial: "\"Responses jumped to 20%+—the templates + rubric made seniors reply instantly.\"",
      author: "Recruiter",
      company: "European tech company (2025)",
      roiData: ""
    },
    ctaText: "DOWNLOAD NOW – Unlock Your Outreach & Screening Hacker Kit"
  },
  upsells: [
    {
      type: "advanced" as const,
      title: "Want to see what Advanced Module 5 actually unlocks?",
      description: "Most recruiters stop here and struggle with low responses/mis-fits. The few who dominate upgrade to the Advanced version – where Module 5 is multi-page with persona/rubric setup (45-min), theory (senior mindset, techniques, engagement, pitfalls), 3 practical tasks (portfolio exercise, outreach campaign, simulation), AI hacks (80% time savings—HireVue), and detailed checklist for 4.7x responses (LinkedIn 2024).",
      ctaText: "Sign up for Advanced Waiting List",
      ctaLink: "#",
      features: [
        "Candidate Persona Form + Evaluation Rubric Setup (weighted scoring 1–5 + frameworks)",
        "Theory: Senior psychology (motivators/regional), beyond keywords (portfolio methods), balanced evaluation (examples), engagement (channels/templates), pitfalls/fixes",
        "3 Tasks: Portfolio assessment, personalised campaign (5 messages), full simulation (questions/rubric/plan)",
        "Expanded Checklist + AI hacks (custom models for 34% better responses)"
      ]
    },
    {
      type: "consulting" as const,
      title: "Still practicing?",
      description: "Book a free 30-minute audit. I'll review one of your outreaches/screenings (or from the Free kit) and show you – line by line – exactly where Advanced changes everything.",
      ctaText: "BOOK FREE 30-MIN AUDIT – ENGAGE & SCREEN SENIORS LIKE NEVER BEFORE",
      ctaLink: "https://calendly.com/_abaddion/30min"
    }
  ],
  quizQuestions: [
    {
      id: 1,
      question: "Which tool is best for technical assessments in CZ?",
      options: ["Photoshop", "Codility", "Excel", "Canva"],
      correct: 1,
      explanation: "Codility is ideal for technical assessments in the Czech Republic, offering coding tests (e.g., Python for AI engineers) and tailored challenges for real-world problems."
    },
    {
      id: 2,
      question: "What's a key to engaging CZ seniors?",
      options: ["Generic emails", "Personalised outreach", "In-office mandates", "Low salaries"],
      correct: 1,
      explanation: "Personalised outreach is crucial. Reference specific work (e.g., GitHub commits), keep emails under 100 words, and emphasize work-life balance (70% prefer flexibility)."
    },
    {
      id: 3,
      question: "What's a common pitfall in screening seniors?",
      options: ["Using GitHub portfolios", "Over-relying on ATS filters", "Offering flexible interviews", "Tailoring assessments"],
      correct: 1,
      explanation: "Over-relying on ATS filters is a major pitfall. In CZ, 100+ applicants per role clog filters; in the US, 200+. Seniors bypass generic postings, so manually review GitHub/LinkedIn profiles."
    },
    {
      id: 4,
      question: "What's a US senior-friendly interview option?",
      options: ["9 AM only", "Virtual evenings", "In-person only", "Weekday mornings"],
      correct: 1,
      explanation: "Virtual evenings and weekend options accommodate busy principals in the US market, demonstrating flexibility that senior candidates value."
    },
    {
      id: 5,
      question: "What weight should leadership/culture have in screening?",
      options: ["10%", "30%", "50%", "80%"],
      correct: 1,
      explanation: "A balanced approach weights skills (40%), experience (30%), and leadership/culture (30%). This ensures you assess technical mastery, relevant experience, and cultural fit."
    }
  ]
};

export const module6Config = {
  title: "Building a Talent Pipeline",
  description: "Module 6: Building a Talent Pipeline - Waiting for applicants = 60+ days wasted. Proactive pipelines (20–50 pre-vetted seniors) ensure ready talent via databases (Airtable/Notion) + nurturing (monthly check-ins). This module teaches basics for CZ/US: goals, tracking, sourcing/nurturing plans, market tips. Reduce time-to-hire 30–40%.",
  duration: "45 min",
  sections: [
    {
      id: -1,
      title: "Before You Start",
      shortTitle: "Before Start",
      icon: Award,
      content: "beforeStart"
    },
    {
      id: 0,
      title: "Welcome to Building a Talent Pipeline",
      shortTitle: "Welcome",
      icon: BookOpen,
      content: "welcome"
    },
    {
      id: 1,
      title: "Pipeline Basics: Setting Up Your Database",
      shortTitle: "Basics",
      icon: Target,
      content: `
        <p style="margin-bottom: 2rem;">A talent pipeline is a database of 20–50 pre-vetted senior candidates, ready to engage when roles open. It cuts time-to-hire by 30–40% in competitive markets.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <div class="challenge-content">
              <h3>Tools</h3>
              <p>Use Airtable or Notion for easy tracking. Columns: Name, Skills (e.g., Python, Kubernetes), Contact (LinkedIn, email), Last Outreach (date), Status (e.g., Interested, Not Interested).</p>
            </div>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 Czech Republic</h4>
            </div>
            <p>Focus on Prague/Brno talent pools (15,000 new IT jobs in 2025). Source seniors from niche roles like AI engineers (€78K) or cybersecurity specialists.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 United States</h4>
            </div>
            <p>Target tech hubs (San Francisco, Seattle) or remote candidates (317,700 annual IT openings). Prioritise principals like cloud architects ($177K total comp).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Setup Tips</h4>
            </div>
            <p>Start with 10–15 candidates per role (e.g., 10 AI engineers, 10 devops specialists). Vet for 10+ years experience and leadership (e.g., led cloud migrations).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Case Insight</h3>
              <p>For a Swiss cybersecurity client, we built an Airtable pipeline of 30 senior devs, tracking their Kubernetes skills and outreach status, landing a principal hire in 30 days.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 2,
      title: "Nurture Relationships: Keep Candidates Warm",
      shortTitle: "Nurture",
      icon: Users,
      content: `
        <p style="margin-bottom: 2rem;">Regular engagement keeps candidates interested, reducing time-to-hire when roles open.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <rect width="20" height="16" x="2" y="4" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
              </div>
              <h4>Monthly Check-Ins</h4>
            </div>
            <p><strong>Czech Republic:</strong> Send emails like: "Hi [Name], a new AI role opened at [company]. Your Python expertise would be highly valuable—interested?" Share CzechCrunch newsletters for local tech updates.</p>
            <p><strong>United States:</strong> Use: "Hi [Name], a principal cloud role opened at [company] with 0.2% equity. Your AWS work aligns well—let's talk?" Share TechCrunch articles for industry trends.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h4>Engagement Tactics</h4>
            </div>
            <p>Invite to meetups (e.g., Prague Devs, SF Bay Area Devs) or webinars (e.g., "AI Trends 2025"). Comment on their GitHub commits to build rapport.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Tips</h4>
            </div>
            <p>Personalise messages (e.g., reference their TensorFlow repo), keep outreach brief (under 100 words), and log interactions in Airtable/Notion.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Case Insight</h3>
              <p>For a security tech giant, we nurtured a Cybersecurity expert via monthly emails about cybersecurity trends, securing their hire when a principal role opened.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 3,
      title: "CZ-Specific Tips",
      shortTitle: "CZ Tips",
      icon: TrendingUp,
      content: `
        <p style="margin-bottom: 2rem;">Czech Republic strategies for building effective talent pipelines.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <h4>Target Prague/Brno</h4>
            </div>
            <p>Source from Prague Devs (500+ members) or Brno Tech Talks (200+ attendees). Focus on AI, cybersecurity, and devops (63% shortage).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="2" x2="12" y2="6"/>
                  <line x1="12" y1="18" x2="12" y2="22"/>
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
                  <line x1="2" y1="12" x2="6" y2="12"/>
                  <line x1="18" y1="12" x2="22" y2="12"/>
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                </svg>
              </div>
              <h4>Expat Communities</h4>
            </div>
            <p>Engage via LinkedIn groups like "Prague Tech Expat Network" (1,000+ members). Highlight CZ's work-life balance (70% hybrid/remote) and lower salaries (€60K–€100K vs. US $150K–$250K).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <h4>Local Events</h4>
            </div>
            <p>Attend Czech ICT Summit to connect with seniors. Offer coffee chats to discuss hybrid roles.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Example</h3>
              <p>For a CZ startup, we sourced 10 AI engineers from Prague Devs, adding them to a pipeline that filled a role in 25 days.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 4,
      title: "US-Specific Tips",
      shortTitle: "US Tips",
      icon: Globe,
      content: `
        <p style="margin-bottom: 2rem;">United States strategies for building effective talent pipelines.</p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <h4>Target Tech Hubs/Remote</h4>
            </div>
            <p>Source from San Francisco, Seattle, or remote-friendly candidates via GitHub or IEEE networks. Focus on AI, cloud, and fintech (74% shortage).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Professional Networks</h4>
            </div>
            <p>Engage via ACM or Women Who Code groups (10,000+ members). Highlight equity (0.2–0.5%) and enterprise projects.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <h4>US Events</h4>
            </div>
            <p>Network at TechCrunch Disrupt (5,000+ attendees) or AWS Re:Invent. Offer virtual meetups to discuss high-impact roles.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Example</h3>
              <p>For a US startup, we built a pipeline of 15 cloud architects via GitHub, landing a hire in 35 days with a tailored equity offer.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 5,
      title: "Activity: Create Your Pipeline Plan",
      shortTitle: "Activity",
      icon: FileText,
      content: "activity"
    },
    {
      id: 6,
      title: "Quiz: Test Your Pipeline Knowledge",
      shortTitle: "Quiz",
      icon: Trophy,
      content: "quiz"
    },
    {
      id: 7,
      title: "Takeaway: Senior IT Talent Pipeline Template",
      shortTitle: "Takeaway",
      icon: Download,
      content: "takeaway"
    }
  ],
  resources: [
    {
      title: "Pipeline Hacker Kit",
      type: "pdf" as const,
      url: "pipeline-hacker-kit.pdf",
      downloadable: true
    }
  ],
  pdfTeaser: {
    title: "Before You Start",
    subtitle: "Reactive hiring loses seniors.",
    description: "The Pipeline Hacker Kit (template) you're about to download is the exact basics I used with cybersecurity firms (specialist analyst pipelines for instant hires) and high-tech companies (C++ pool nurtured for weeks-fast closes). Without it → empty pipelines, slow hires. With it → goals + tracking to build your first 20-candidate pool today.",
    urgency: {
      text: "Required for the pipeline plan activity + quiz.",
      bonus: "Limited-time bonus: download within 24 hours and get a free 15-minute pipeline review with me (normally reserved for private clients)."
    },
    teasers: {
      title: "Exact preview – taken word-for-word from your Free PDF (no hype, only what's really inside):",
      items: [
        "Prep for Pipeline Building (30-min activity): Pipeline Goals Form (role/volume/quality/time/channels/cadence), Candidate Tracking Guide (Airtable/Notion fields: Name/Role/Skills/Contact/Last Touchpoint/Status)",
        "Pipeline Strategies (45-min activity): Sourcing Plan (CZ: Prague Devs/GitHub/LinkedIn; US: IEEE/StackOverflow/GitHub, AI like Gloat), Nurturing Plan (monthly emails/events, personalization)",
        "Best Practices (5-min checklist): Set goals/AI tools/communities/nurture regularly/track metrics/authenticity/test channels/review feedback, CZ (e.g., gaming client 20-candidate Notion pipeline → 3 hires in 30 days)",
        "CZ/US Tips: Hybrid/learning CZ, equity/enterprise US"
      ]
    },
    socialProof: {
      testimonial: "\"First pipeline with 20 seniors ready—hires in 30 days became real.\"",
      author: "Recruiter",
      company: "European tech company (2025)",
      roiData: ""
    },
    ctaText: "DOWNLOAD NOW – Unlock Your Pipeline Hacker Kit (Template Basics)"
  },
  upsells: [
    {
      type: "advanced" as const,
      title: "Want to see what Advanced Module 6 actually unlocks?",
      description: "Most recruiters stop here and stay reactive. The few who dominate upgrade to the Advanced version – where Module 6 is multi-page with goals/tracking guides, sourcing/nurturing/qualification, case studies (Prague AI 3 hires/40 days, Seattle cloud 2 hires/35 days), 3 practical tasks (build pipeline, nurturing plan, market strategies), advanced techniques (AI automation/predictive analytics/community building/long-term relationships) for 30–40% faster hires.",
      ctaText: "Sign up for Advanced Waiting List",
      ctaLink: "#",
      features: [
        "Pipeline Goals Form + Candidate Tracking Guide (advanced fields/KPIs)",
        "Sourcing/Nurturing/Qualification Plans: Multi-channel + rubric (40% skills etc.)",
        "Case Studies + Templates: Tracking sheets, outreach/nurturing emails, rubric",
        "3 Tasks: Full pipeline build, 90-day nurturing, market-specific strategies",
        "Advanced Techniques: AI (HireEZ 217% leads), predictive, community (Slack/webinars), long-term management"
      ]
    },
    {
      type: "consulting" as const,
      title: "Still planning your first pipeline?",
      description: "Book a free 30-minute audit. I'll review your current pipeline (or from the Free kit) and show you – step by step – exactly where Advanced changes everything.",
      ctaText: "BOOK FREE 30-MIN AUDIT – BUILD A PIPELINE THAT NEVER RUNS DRY",
      ctaLink: "https://calendly.com/_abaddion/30min"
    }
  ],
  quizQuestions: [
    {
      id: 1,
      question: "What's a good tool for a talent pipeline database?",
      options: ["Photoshop", "Airtable", "Word", "PowerPoint"],
      correct: 1,
      explanation: "Airtable is an excellent tool for building talent pipelines, offering easy tracking with columns for Name, Skills, Contact, Last Outreach, and Status."
    },
    {
      id: 2,
      question: "How many candidates should a CZ pipeline include per role?",
      options: ["1–5", "10–15", "50–100", "200+"],
      correct: 1,
      explanation: "Start with 10–15 candidates per role in the Czech Republic. This provides a good pool while remaining manageable for regular nurturing."
    },
    {
      id: 3,
      question: "What's a nurturing tactic for US seniors?",
      options: ["Generic job ads", "TechCrunch article shares", "In-office mandates", "Cold calls"],
      correct: 1,
      explanation: "Sharing TechCrunch articles is an effective nurturing tactic for US seniors, keeping them engaged with industry trends and demonstrating your awareness of the market."
    },
    {
      id: 4,
      question: "Which CZ community is ideal for sourcing?",
      options: ["Prague Devs", "Fashion Week", "Food Festival", "Comic-Con"],
      correct: 0,
      explanation: "Prague Devs (500+ members) is an ideal CZ community for sourcing senior IT talent, focusing on AI, cybersecurity, and devops."
    },
    {
      id: 5,
      question: "How fast did our Swiss cybersecurity client's pipeline enable a senior hire?",
      options: ["60 days", "90 days", "30 days", "120 days"],
      correct: 2,
      explanation: "Our Swiss cybersecurity client's Airtable pipeline of 30 senior devs enabled a principal hire in just 30 days, demonstrating the effectiveness of proactive pipeline building."
    }
  ]
};

export const module7Config = {
  title: "Measuring and Optimising Recruitment Success",
  description: "Guessing what works = slow hires, wasted budget. Track basics (time-to-hire <60 days, acceptance >80%, quality 90%) with simple tools (Google Sheets/ATS) to optimise channels and cut costs. This module teaches metrics framework for CZ/US: select/track/analyse/iterate for 30–40% faster, better hires.",
  duration: "30 min",
  sections: [
    {
      id: -1,
      title: "Before You Start",
      shortTitle: "Before Start",
      icon: Award,
      content: "beforeStart"
    },
    {
      id: 0,
      title: "Welcome to Measuring and Optimising Recruitment Success",
      shortTitle: "Welcome",
      icon: BookOpen,
      content: "welcome"
    },
    {
      id: 1,
      title: "Key Metrics (applicants/shortlisted/time-to-hire/acceptance/quality)",
      shortTitle: "Key Metrics",
      icon: Target,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Track basics: time-to-hire <60 days, acceptance >80%, quality 90%. These metrics reveal bottlenecks and guide optimisation.
        </p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Time-to-Hire</h4>
            </div>
            <p><strong>Target:</strong> <60 days for seniors.</p>
            <p><strong>Czech Republic:</strong> Average is 90 days due to 63% talent shortages and undertrained TA teams. Aim for 45–60 days with proactive pipelines.</p>
            <p><strong>United States:</strong> Average is 70 days, slowed by Big Tech competition. Target 40–55 days with targeted sourcing.</p>
            <p><strong>How to track:</strong> Measure from job posting to offer acceptance. Use ATS (Workable/Lever) or Google Sheets.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h4>Offer Acceptance Rate</h4>
            </div>
            <p><strong>Target:</strong> >80%.</p>
            <p><strong>CZ:</strong> Seniors prioritise hybrid work (70% preference) and transparency (€60K–€100K). Low offers drop acceptance to 60%.</p>
            <p><strong>US:</strong> Equity (0.2–0.5%) and enterprise projects drive >80% acceptance. Big Tech counteroffers lower rates to 65%.</p>
            <p><strong>How to track:</strong> (Accepted offers / Total offers) × 100. Track in ATS or spreadsheet.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <h4>Candidate Quality</h4>
            </div>
            <p><strong>Target:</strong> 90% passing technical interviews (e.g., Codility for CZ, HackerRank for US).</p>
            <p><strong>CZ:</strong> Only 10% of 100+ applicants per role are senior-qualified due to generic ATS filters.</p>
            <p><strong>US:</strong> 15% of 200+ applicants are senior-qualified, with GitHub portfolios key to quality.</p>
            <p><strong>How to track:</strong> (Passed technical interviews / Total interviews) × 100. Review monthly.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 20V10"/>
                  <path d="M12 20V4"/>
                  <path d="M6 20v-6"/>
                </svg>
              </div>
              <h4>Applicants & Shortlisted</h4>
            </div>
            <p><strong>Applicants:</strong> Total applications per role. Track by source (LinkedIn, GitHub, Jobs.cz, etc.).</p>
            <p><strong>Shortlisted:</strong> Candidates who pass initial screening. Target: 20–30% of applicants.</p>
            <p><strong>How to track:</strong> Use ATS filters or Google Sheets with columns: Source, Date Applied, Status.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Real feedback from recruiters who used this exact template:</h3>
              <p>"Finally saw why time-to-hire was 90 days—shifts cut it to 58 instantly." – Recruiter, European tech company (2025)</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 2,
      title: "Tools (LinkedIn Talent Insights/Google Analytics/Workable/Lever)",
      shortTitle: "Tools",
      icon: TrendingUp,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Simple tools (Google Sheets/ATS) help you track and optimise. No complex setup needed—start today.
        </p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect x="2" y="9" width="4" height="12"/>
                  <circle cx="4" cy="4" r="2"/>
                </svg>
              </div>
              <h4>LinkedIn Talent Insights</h4>
            </div>
            <p><strong>CZ:</strong> Benchmark against your most relevant competitors or complimentary firms to identify sourcing gaps (e.g., low AI engineer applicants). Track applicant demographics (e.g., 10+ years experience).</p>
            <p><strong>US:</strong> Compare with your relevant competitors and complimentary companies to refine channels (e.g., shift from Indeed to GitHub). Monitor talent pool trends (e.g., cloud architects in Seattle).</p>
            <p><strong>Setup:</strong> Free tier available. Connect your LinkedIn Recruiter account.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
              </div>
              <h4>Google Analytics</h4>
            </div>
            <p><strong>CZ/US:</strong> Track career page traffic (e.g., shipmonk.com/careers). Measure clicks on "Apply" buttons and time spent on senior role postings.</p>
            <p><strong>Tips:</strong> Set up goals (e.g., 50 senior applicants/month) and track referral sources (e.g., Prague Devs vs. TechCrunch).</p>
            <p><strong>Setup:</strong> Free. Add tracking code to your career page. See CZ/US tips (e.g., Prague SaaS-inspired Google Analytics traffic boost).</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  <rect width="20" height="14" x="2" y="6" rx="2"/>
                </svg>
              </div>
              <h4>ATS Dashboards (Workable/Lever)</h4>
            </div>
            <p><strong>Workable (CZ):</strong> Monitor applicants, shortlisted candidates, and time-to-hire. Export data to Google Sheets for analysis.</p>
            <p><strong>Lever (US):</strong> Built-in analytics for source tracking and funnel analysis. Integrate with Airtable for pipeline tracking.</p>
            <p><strong>Setup:</strong> Use built-in reports or export to Google Sheets/Airtable for custom dashboards.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 20V10"/>
                  <path d="M12 20V4"/>
                  <path d="M6 20v-6"/>
                </svg>
              </div>
              <h4>Google Sheets / Airtable</h4>
            </div>
            <p><strong>Simple tracking:</strong> Create columns: Role, Source, Applicants, Shortlisted, Time-to-Hire, Acceptance Rate, Quality Score.</p>
            <p><strong>Dashboard template:</strong> Download the Metrics Hacker Kit (included in PDF) for a ready-to-use template.</p>
            <p><strong>Review cadence:</strong> Update weekly, analyse monthly.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Example</h3>
              <p>A Prague SaaS firm used Google Analytics to track a 20% increase in career page traffic after adding Kubernetes details, boosting senior applicants.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 3,
      title: "Metrics Framework (Dashboard Template)",
      shortTitle: "Framework",
      icon: FileText,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          The Metrics Hacker Kit dashboard template includes: applicants/shortlisted/time-to-hire/acceptance/quality. Use it to track and analyse your data.
        </p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <h4>Dashboard Template Structure</h4>
            </div>
            <p><strong>Columns to track:</strong></p>
            <ul style="margin-left: 1.5rem; padding-left: 0; line-height: 1.8;">
              <li><strong>Role:</strong> Job title (e.g., Senior AI Engineer)</li>
              <li><strong>Source:</strong> Channel (LinkedIn, GitHub, Jobs.cz, Referral, etc.)</li>
              <li><strong>Applicants:</strong> Total applications received</li>
              <li><strong>Shortlisted:</strong> Candidates who passed initial screening</li>
              <li><strong>Time-to-Hire:</strong> Days from posting to offer acceptance</li>
              <li><strong>Acceptance Rate:</strong> (Accepted / Total offers) × 100</li>
              <li><strong>Quality Score:</strong> % passing technical interviews</li>
            </ul>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                ${ORANGE_LIGHTBULB_ICON}
              </div>
              <h4>Analysis Tips</h4>
            </div>
            <p><strong>If time-to-hire >60 days:</strong> Shift channels (e.g., low quality → GitHub). Increase nurturing emails or improve job descriptions.</p>
            <p><strong>If acceptance <80%:</strong> Add hybrid/equity options. Review offer competitiveness (CZ: €60K–€100K, US: equity 0.2–0.5%).</p>
            <p><strong>If quality <90%:</strong> Tighten screening criteria. Review source effectiveness—shift from low-quality channels.</p>
            <p><strong>Review cadence:</strong> Monthly analysis. Weekly updates for urgent roles.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 CZ Tailoring</h4>
            </div>
            <p><strong>Beat 90-day average:</strong> Track sources that deliver <60 days. Focus on GitHub, Prague Devs, referrals.</p>
            <p><strong>Quality focus:</strong> Only 10% of applicants are senior-qualified. Track which sources deliver the highest quality.</p>
            <p><strong>Example:</strong> If Jobs.cz yields <10% senior-qualified, shift budget to GitHub outreach.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 US Tailoring</h4>
            </div>
            <p><strong>Equity for acceptance:</strong> Track acceptance rates by offer type. Equity (0.2–0.5%) drives >80% acceptance.</p>
            <p><strong>Beat Big Tech:</strong> Monitor time-to-hire vs. competitors. Speed (72-hour offers) and equity beat slow processes.</p>
            <p><strong>Example:</strong> If Indeed delivers <15% senior-qualified, focus on IEEE networks or TechCrunch Disrupt.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Hands-On Activity (you need the PDF)</h3>
              <p>Open the kit → complete 30-min Prep (fill Metrics Selection Form for a role) + 45-min Framework (enter sample data in template, note 1 optimisation) → submit async for optional feedback. Earn the "Data-Driven Optimiser" badge.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 4,
      title: "Best Practices (15-min checklist)",
      shortTitle: "Best Practices",
      icon: Users,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Select metrics/AI tools/track sources/review monthly/integrate ATS/authenticity/test channels/act on insights. CZ/US tips included.
        </p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h4>Best Practices Checklist</h4>
            </div>
            <ul style="margin-left: 1.5rem; padding-left: 0; line-height: 1.8;">
              <li><strong>Select metrics:</strong> Start with basics (time-to-hire, acceptance, quality). Add more as you scale.</li>
              <li><strong>AI tools:</strong> Use HireEZ, Gloat, or LinkedIn Recruiter for sourcing. Don't rely solely on ATS filters.</li>
              <li><strong>Track sources:</strong> Every applicant gets tagged with source (LinkedIn, GitHub, Jobs.cz, Referral, etc.).</li>
              <li><strong>Review monthly:</strong> Analyse trends. Identify bottlenecks (e.g., time-to-hire >60 days).</li>
              <li><strong>Integrate ATS:</strong> Export data from Workable/Lever to Google Sheets/Airtable for analysis.</li>
              <li><strong>Authenticity:</strong> Don't game metrics. Track real data to find real problems.</li>
              <li><strong>Test channels:</strong> A/B test sources (e.g., LinkedIn vs. GitHub). Shift budget to winners.</li>
              <li><strong>Act on insights:</strong> If GitHub delivers 90% quality vs. Jobs.cz 10%, shift focus. Don't just track—optimise.</li>
            </ul>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 CZ Tips</h4>
            </div>
            <p><strong>Prague SaaS-inspired Google Analytics traffic boost:</strong> Add tech stack details (Kubernetes, Python) to career pages. Track traffic increases.</p>
            <p><strong>Beat 90-day average:</strong> Use proactive pipelines (Module 6) to cut time-to-hire to 45–60 days.</p>
            <p><strong>Quality focus:</strong> Only 10% of applicants are senior-qualified. Track sources that deliver quality (GitHub, Prague Devs).</p>
            <p><strong>Hybrid work:</strong> 70% prefer hybrid/remote. Track acceptance rates by work model.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 US Tips</h4>
            </div>
            <p><strong>Equity for acceptance:</strong> Track acceptance rates by offer type. Equity (0.2–0.5%) drives >80% acceptance.</p>
            <p><strong>Beat Big Tech:</strong> Speed (72-hour offers) and equity beat slow processes. Track time-to-hire vs. competitors.</p>
            <p><strong>Quality focus:</strong> 15% of applicants are senior-qualified. GitHub portfolios key to quality.</p>
            <p><strong>Enterprise projects:</strong> Highlight high-impact roles. Track which project types drive acceptance.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Remember</h3>
              <p>Metrics don't lie—bad ones cost hires. Start tracking the right ones. Review monthly, act on insights, and iterate for 30–40% faster, better hires.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 5,
      title: "Hands-On Activity (you need the PDF)",
      shortTitle: "Activity",
      icon: FileText,
      content: "activity"
    },
    {
      id: 6,
      title: "Quiz – 5 questions taken verbatim from the template",
      shortTitle: "Quiz",
      icon: Trophy,
      content: "quiz"
    },
    {
      id: 7,
      title: "Takeaway: Metrics Hacker Kit (Dashboard Template Basics)",
      shortTitle: "Takeaway",
      icon: Download,
      content: "takeaway"
    }
  ],
  resources: [
    {
      title: "Metrics Hacker Kit (Dashboard Template Basics)",
      type: "pdf" as const,
      url: "metrics-hacker-kit.pdf",
      downloadable: true
    }
  ],
  pdfTeaser: {
    title: "Before You Start",
    subtitle: "Blind hiring loses money.",
    description: "The Metrics Hacker Kit (dashboard template) you're about to download is the exact basics I used with cybersecurity firms (channel shifts optimising analyst pipelines) and high-tech companies (data cutting time-to-hire). Without it → vanity metrics, no action. With it → plug-and-play template to start tracking and improving today.",
    urgency: {
      text: "Required for the dataset activity + quiz.",
      bonus: "Limited-time bonus: download within 24 hours and get a free 15-minute metrics review with me (normally reserved for private clients)."
    },
    teasers: {
      title: "Exact preview – taken word-for-word from your Free PDF (no hype, only what's really inside):",
      items: [
        "Prep for Metrics Tracking (30-min activity): Metrics Selection Form (role/key metrics/tools/review cadence), Data Collection Guide (Workable/Lever/Google Analytics/Airtable)",
        "Metrics Framework (45-min activity): Dashboard Template (applicants/shortlisted/time-to-hire/acceptance/quality), Analysis Tips (if >60 days shift channels, <80% acceptance add hybrid/equity)",
        "Best Practices (15-min checklistHIPS): Select metrics/AI tools/track sources/review monthly/integrate ATS/authenticity/test channels/act on insights, CZ/US tips (e.g., Prague SaaS-inspired Google Analytics traffic boost)",
        "Hands-On Activity (you need the PDF): Open the kit → complete 30-min Prep (fill Metrics Selection Form for a role) + 45-min Framework (enter sample data in template, note 1 optimisation) → submit async for optional feedback. Earn the \"Data-Driven Optimiser\" badge.",
        "Quiz – 5 questions taken verbatim from the template (e.g. \"What's the target time-to-hire?\")."
      ]
    },
    socialProof: {
      testimonial: "\"Finally saw why time-to-hire was 90 days—shifts cut it to 58 instantly.\"",
      author: "Recruiter",
      company: "European tech company (2025)",
      roiData: ""
    },
    ctaText: "DOWNLOAD NOW – Unlock Your Metrics Hacker Kit (Dashboard Template Basics)"
  },
  upsells: [
    {
      type: "advanced" as const,
      title: "Want to see what Advanced Module 7 actually unlocks?",
      description: "Most recruiters stop here and guess optimisations. The few who dominate upgrade to the Advanced version – where Module 7 is multi-page with data collection/analysis frameworks, full metrics (time/quality/efficiency/cost), 4 case studies (CZ fintech 39% reduction, US cybersecurity 45% cost cut, gaming quality boost, SW house savings), 3 practical tasks (build dashboard, analyse channels, optimise funnel), advanced techniques (AI HireEZ 217% leads, predictive, diversity/candidate experience) for €100K–€150K savings.",
      ctaText: "Sign up for Advanced Waiting List",
      ctaLink: "#",
      features: [
        "Metrics Selection Form + Data Collection Guide (advanced tools/setup)",
        "Analysis Frameworks: Visuals (trend lines/bar/funnel/heatmaps), interpretation",
        "Full Metrics: Time (to-hire/fill/sourcing), Quality (pass rate/satisfaction/retention), Efficiency (channel effectiveness/response/application-to-interview), Cost (per-hire/channel)",
        "4 Case Studies + Examples: CZ fintech/US cybersecurity/gaming/SW house with tables/visuals",
        "3 Tasks: Build dashboard, channel analysis, funnel optimisation",
        "Advanced Techniques: AI-powered/predictive/diversity/candidate experience"
      ]
    },
    {
      type: "consulting" as const,
      title: "Still analysing your data?",
      description: "Book a free 30-minute audit. I'll review your current metrics (or from the Free kit) and show you – KPI by KPI – exactly where Advanced changes everything.",
      ctaText: "BOOK FREE 30-MIN AUDIT – OPTIMISE HIRING WITH DATA THAT WINS",
      ctaLink: "https://calendly.com/_abaddion/30min"
    }
  ],
  quizQuestions: [
    {
      id: 1,
      question: "What's the target time-to-hire?",
      options: ["90 days", "<60 days", "120 days", "30 days"],
      correct: 1,
      explanation: "Target <60 days for seniors. While the average is 90 days in CZ and 70 days in US, proactive pipelines and optimised channels can achieve 45–60 days."
    },
    {
      id: 2,
      question: "What's the target offer acceptance rate?",
      options: ["50%", ">80%", "30%", "100%"],
      correct: 1,
      explanation: "Target >80% offer acceptance rate. CZ: Seniors prioritise hybrid work (70% preference) and transparency. US: Equity (0.2–0.5%) and enterprise projects drive >80% acceptance."
    },
    {
      id: 3,
      question: "What's the target candidate quality score?",
      options: ["50%", "70%", "90%", "100%"],
      correct: 2,
      explanation: "Target 90% passing technical interviews. CZ: Only 10% of 100+ applicants per role are senior-qualified. US: 15% of 200+ applicants are senior-qualified. Track sources that deliver quality."
    },
    {
      id: 4,
      question: "Which tool is mentioned for tracking career page traffic?",
      options: ["Photoshop", "Google Analytics", "Word", "Canva"],
      correct: 1,
      explanation: "Google Analytics tracks career page traffic, measuring clicks on 'Apply' buttons and time spent on senior role postings. A Prague SaaS firm used it to track a 20% increase in traffic after adding Kubernetes details."
    },
    {
      id: 5,
      question: "What should you do if time-to-hire exceeds 60 days?",
      options: ["Do nothing", "Shift channels (e.g., low quality → GitHub)", "Lower salaries", "Post more jobs"],
      correct: 1,
      explanation: "If time-to-hire >60 days, shift channels (e.g., low quality → GitHub). Increase nurturing emails or improve job descriptions. Track which sources deliver faster hires."
    }
  ]
};

export const module8Config = {
  title: "Q&A and Action Planning",
  description: "You've mastered sourcing to metrics—now make it real. This final module tackles your company-specific challenges (e.g., competing with giants, low responses) and commits to a 30-day action plan. Turn knowledge into hires: clear actions, owners, deadlines, outcomes for CZ/US markets.",
  duration: "30 min",
  sections: [
    {
      id: -1,
      title: "Before You Start",
      shortTitle: "Before Start",
      icon: Award,
      content: "beforeStart"
    },
    {
      id: 0,
      title: "Welcome to Q&A and Action Planning",
      shortTitle: "Welcome",
      icon: BookOpen,
      content: "welcome"
    },
    {
      id: 1,
      title: "Address Real Hurdles (ATS filters, Big Tech competition)",
      shortTitle: "Hurdles",
      icon: Target,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Common challenges TA teams face: competing with giants, low responses, ATS filters missing seniors. Here's how to tackle them.
        </p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 CZ Challenge: "How do we compete with tech competitors that pay high salaries for seniors?"</h4>
            </div>
            <p><strong>Answer:</strong> Highlight niche projects (e.g., "Lead our AI fraud detection") and faster decision-making (48-hour offers vs. competitor' 2 weeks). Emphasise work-life balance (70% prefer hybrid/remote) and learning budgets (€1K).</p>
            <p><strong>Example:</strong> For a CZ startup, we beat multinationals by showcasing a unique fintech project and hybrid work.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 US Challenge: "How do we stand out against Big Tech offers?"</h4>
            </div>
            <p><strong>Answer:</strong> Offer equity (0.2–0.5%) and high-impact roles (e.g., "Architect our cloud platform at [company]"). Speed up offers (72 hours) and include $5K home office stipends.</p>
            <p><strong>Example:</strong> For a US startup, we secured an AI engineer by emphasizing equity and a 40-day hire cycle.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 CZ Challenge: "Our Jobs.cz postings attract junior applicants."</h4>
            </div>
            <p><strong>Answer:</strong> Shift to GitHub for seniors (e.g., Python repos) and Prague Devs meetups. Use Boolean searches like "senior AND AI AND Prague -junior" on LinkedIn Recruiter.</p>
            <p><strong>Example:</strong> For a gaming company, we sourced a principal devops engineer via GitHub, bypassing junior-heavy postings.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 US Challenge: "Our ATS filters miss senior candidates."</h4>
            </div>
            <p><strong>Answer:</strong> Manually review GitHub portfolios and IEEE profiles. Use AI tools like HireVue to screen for 10+ years experience.</p>
            <p><strong>Example:</strong> For a US firm, we cut time-to-hire by 25% by focusing on portfolio impact over ATS keywords.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
              <h4>Challenge: "Low response rates from senior candidates"</h4>
            </div>
            <p><strong>Answer:</strong> Personalise outreach (reference their GitHub projects), offer value first (share market insights), and use multiple touchpoints (LinkedIn, email, events).</p>
            <p><strong>Example:</strong> For a CZ fintech, we increased response rates from 5% to 25% by referencing candidates' open-source contributions.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Real feedback from recruiters who used this exact template:</h3>
              <p>"Finally committed—3 actions, 2 hires in 45 days. Game-changer." – Recruiter, European tech company (2025)</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 2,
      title: "Prep for Planning (30-min activity)",
      shortTitle: "Prep",
      icon: FileText,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Before building your action plan, identify your challenges and set clear goals. The Action Plan Hacker Kit includes forms to guide you.
        </p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
              </div>
              <h4>Challenge Identification Form</h4>
            </div>
            <p><strong>For each role, identify:</strong></p>
            <ul style="margin-left: 1.5rem; padding-left: 0; line-height: 1.8;">
              <li><strong>Role:</strong> Job title (e.g., Senior AI Engineer)</li>
              <li><strong>Challenges:</strong> List top 3 hurdles (e.g., competing with Big Tech, low response rates, ATS filters)</li>
              <li><strong>Metrics:</strong> Current state (e.g., time-to-hire: 90 days, acceptance: 60%)</li>
              <li><strong>Targets:</strong> Desired outcomes (e.g., <60 days, >80% acceptance)</li>
            </ul>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <h4>Goal Setting Guide</h4>
            </div>
            <p><strong>Set measurable goals:</strong></p>
            <ul style="margin-left: 1.5rem; padding-left: 0; line-height: 1.8;">
              <li><strong>Specific:</strong> "Reduce time-to-hire from 90 to 60 days" (not "hire faster")</li>
              <li><strong>Measurable:</strong> Use numbers (e.g., 20% more applicants, 3 hires)</li>
              <li><strong>Achievable:</strong> Realistic given resources (e.g., 1 recruiter, 2 roles)</li>
              <li><strong>Time-bound:</strong> 30-day deadline with weekly check-ins</li>
            </ul>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Example</h3>
              <p><strong>Role:</strong> Senior DevOps Engineer<br/>
              <strong>Challenge:</strong> Competing with JetBrains for Prague talent<br/>
              <strong>Current:</strong> 90 days time-to-hire, 60% acceptance<br/>
              <strong>Target:</strong> 60 days, 80% acceptance</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 3,
      title: "Action Plan Framework (45-min activity)",
      shortTitle: "Action Plan",
      icon: TrendingUp,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Build a 30-day plan with 3 actions for <60-day hires. Use the template: Action/Owner/Deadline/Outcome.
        </p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--orange);">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
              </div>
              <h4>Template Structure</h4>
            </div>
            <p><strong>Columns in the Action Plan Template:</strong></p>
            <ul style="margin-left: 1.5rem; padding-left: 0; line-height: 1.8;">
              <li><strong>Action:</strong> Specific task (e.g., "Redesign job description highlighting Kubernetes")</li>
              <li><strong>Owner:</strong> Person responsible (e.g., "Sarah, TA Manager")</li>
              <li><strong>Deadline:</strong> Date (e.g., "Week 1", "Day 7")</li>
              <li><strong>Outcome:</strong> Measurable result (e.g., "20% more applicants", "3 qualified candidates")</li>
            </ul>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 CZ Example: 3 Actions for <60-Day Hires</h4>
            </div>
            <ol style="margin-left: 1.5rem; padding-left: 0; line-height: 1.8;">
              <li><strong>Action:</strong> Revise job descriptions to highlight tech stacks (Python, Kubernetes) and hybrid work (70% preference)<br/>
              <strong>Owner:</strong> TA Manager<br/>
              <strong>Deadline:</strong> Week 1<br/>
              <strong>Outcome:</strong> 20% more senior applicants</li>
              <li><strong>Action:</strong> Join Prague Devs or Brno Tech Talks to source seniors (500+ members)<br/>
              <strong>Owner:</strong> Recruiter<br/>
              <strong>Deadline:</strong> Week 2<br/>
              <strong>Outcome:</strong> 10 qualified candidates added to pipeline</li>
              <li><strong>Action:</strong> Test GitHub outreach for AI or devops candidates (€60K–€100K roles)<br/>
              <strong>Owner:</strong> Recruiter<br/>
              <strong>Deadline:</strong> Week 3<br/>
              <strong>Outcome:</strong> 5 responses, 2 interviews scheduled</li>
            </ol>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 US Example: 3 Actions for <60-Day Hires</h4>
            </div>
            <ol style="margin-left: 1.5rem; padding-left: 0; line-height: 1.8;">
              <li><strong>Action:</strong> Update career pages with equity offers (0.2%) and enterprise projects<br/>
              <strong>Owner:</strong> Marketing + TA<br/>
              <strong>Deadline:</strong> Week 1<br/>
              <strong>Outcome:</strong> 30% more senior applicants</li>
              <li><strong>Action:</strong> Network at TechCrunch Disrupt or IEEE events to engage cloud architects ($150K–$250K)<br/>
              <strong>Owner:</strong> TA Manager<br/>
              <strong>Deadline:</strong> Week 2<br/>
              <strong>Outcome:</strong> 15 qualified candidates added to pipeline</li>
              <li><strong>Action:</strong> Implement AI screening tools like Gloat to boost candidate quality (90% pass rate)<br/>
              <strong>Owner:</strong> TA Manager<br/>
              <strong>Deadline:</strong> Week 3<br/>
              <strong>Outcome:</strong> 50% reduction in screening time</li>
            </ol>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2a6 6 0 0 0-6 6c0 2.2 1.1 4.2 2.9 5.4l.1.1a2 2 0 0 1 .9 1.6V17h4v-1.9a2 2 0 0 1 .9-1.6l.1-.1C17 12.2 18 10.2 18 8a6 6 0 0 0-6-6z"/>
                  <path d="M9 18h6"/>
                  <path d="M10 22h4"/>
                </svg>
              </div>
              <h4>Tips</h4>
            </div>
            <p>Assign owners, set weekly deadlines, and track metrics (e.g., time-to-hire <60 days, >80% acceptance rate). Review progress at day 30 to iterate.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Case Insight</h3>
              <p>For a Brno startup, we planned three actions—redesigning career pages, joining Prague Devs, and testing GitHub outreach—cutting time-to-hire from 90 to 50 days for a senior devops engineer.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 4,
      title: "Best Practices (15-min checklist)",
      shortTitle: "Best Practices",
      icon: Users,
      content: `
        <p class="lead-text" style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.6; color: var(--dark);">
          Identify challenges/set goals/assign owners/use AI tools/engage communities/nurture/track/review. CZ/US tips included.
        </p>
        
        <div class="challenge-cards" style="display: flex; flex-direction: column; gap: 1.5rem;">
          <div class="challenge-card">
            <div class="challenge-card-header">
              <div class="challenge-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h4>Best Practices Checklist</h4>
            </div>
            <ul style="margin-left: 1.5rem; padding-left: 0; line-height: 1.8;">
              <li><strong>Identify challenges:</strong> Use Challenge Identification Form to list top 3 hurdles per role.</li>
              <li><strong>Set goals:</strong> Use Goal Setting Guide (SMART: Specific, Measurable, Achievable, Time-bound).</li>
              <li><strong>Assign owners:</strong> Each action has one owner. No shared ownership—clarity drives results.</li>
              <li><strong>Use AI tools:</strong> HireEZ, Gloat, LinkedIn Recruiter for sourcing. Don't rely solely on ATS filters.</li>
              <li><strong>Engage communities:</strong> Prague Devs (CZ), IEEE/TechCrunch Disrupt (US). Build relationships before hiring.</li>
              <li><strong>Nurture:</strong> Monthly check-ins with pipeline candidates (Module 6). Keep them warm.</li>
              <li><strong>Track:</strong> Use metrics dashboard (Module 7). Review weekly for urgent roles, monthly for others.</li>
              <li><strong>Review:</strong> Day 30 review. What worked? What didn't? Iterate for next 30 days.</li>
            </ul>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇨🇿 CZ Tips</h4>
            </div>
            <p><strong>Hybrid/Prague Devs:</strong> 70% prefer hybrid/remote. Engage via Prague Devs (500+ members) or Brno Tech Talks.</p>
            <p><strong>Example:</strong> Swiss cryptocurrency-inspired pipeline example—build 20-candidate pool, nurture monthly, land hires in 30–40 days.</p>
            <p><strong>Beat 90-day average:</strong> Use proactive pipelines (Module 6) and optimised channels (Module 7) to cut time-to-hire to 45–60 days.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-card-header">
              <h4>🇺🇸 US Tips</h4>
            </div>
            <p><strong>Equity/IEEE:</strong> Equity (0.2–0.5%) drives >80% acceptance. Engage via IEEE networks or TechCrunch Disrupt.</p>
            <p><strong>Beat Big Tech:</strong> Speed (72-hour offers) and equity beat slow processes. Track time-to-hire vs. competitors.</p>
            <p><strong>Enterprise projects:</strong> Highlight high-impact roles. Track which project types drive acceptance.</p>
          </div>
          
          <div class="challenge-card">
            <div class="challenge-icon">
              ${ORANGE_LIGHTBULB_ICON}
            </div>
            <div class="challenge-content">
              <h3>Remember</h3>
              <p>Learning without action = zero hires. Turn knowledge into results. With the Action Plan Hacker Kit, you have a structured plan to transform your hiring in 30 days.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: 5,
      title: "Hands-On Activity (you need the PDF)",
      shortTitle: "Activity",
      icon: FileText,
      content: "activity"
    },
    {
      id: 6,
      title: "Quiz – 5 questions taken verbatim from the template",
      shortTitle: "Quiz",
      icon: Trophy,
      content: "quiz"
    },
    {
      id: 7,
      title: "Takeaway: Action Plan Hacker Kit (30-Day Template Basics)",
      shortTitle: "Takeaway",
      icon: Download,
      content: "takeaway"
    }
  ],
  resources: [
    {
      title: "Action Plan Hacker Kit (30-Day Template Basics)",
      type: "pdf" as const,
      url: "action-plan-hacker-kit.pdf",
      downloadable: true
    }
  ],
  pdfTeaser: {
    title: "Before You Start",
    subtitle: "Learning without action = zero hires.",
    description: "The Action Plan Hacker Kit (template) you're about to download is the exact basics I used with cybersecurity firms (30-day plans turning pipelines into specialist analyst hires) and high-tech companies (Q&A-driven actions closing C++ roles fast). Without it → good intentions, no results. With it → structured plan to transform your hiring in 30 days.",
    urgency: {
      text: "Required for the planning activity + quiz.",
      bonus: "Limited-time bonus: download within 24 hours and get a free 15-minute action plan review with me (normally reserved for private clients)."
    },
    teasers: {
      title: "Exact preview – taken word-for-word from your Free PDF (no hype, only what's really inside):",
      items: [
        "Prep for Planning (30-min activity): Challenge Identification Form (role/challenges/metrics/targets), Goal Setting Guide",
        "Action Plan Framework (45-min activity): Template (Action/Owner/Deadline/Outcome, e.g., redesign JD/week 1/20% more apps)",
        "Best Practices (15-min checklist): Identify challenges/set goals/assign owners/use AI tools/engage communities/nurture/track/review, CZ/US tips (e.g., Swiss cryptocurrency-inspired pipeline example)",
        "Hands-On Activity (you need the PDF): Open the kit → complete 30-min Prep (fill Challenge Form for your biggest issue) + 45-min Framework (build 3-action plan) → submit async for optional feedback. Earn the \"Transformation Closer\" badge + course completion certificate.",
        "Quiz – 5 questions taken verbatim from the template (e.g. \"Name a column in the Action Plan Template\")."
      ]
    },
    socialProof: {
      testimonial: "\"Finally committed—3 actions, 2 hires in 45 days. Game-changer.\"",
      author: "Recruiter",
      company: "European tech company (2025)",
      roiData: ""
    },
    ctaText: "DOWNLOAD NOW – Unlock Your Action Plan Hacker Kit (30-Day Template Basics)"
  },
  upsells: [
    {
      type: "advanced" as const,
      title: "Want the full transformation?",
      description: "You've built skills to source, screen, brand, pipeline, and optimise senior hires. With these basics, you're already beating average (e.g., <90-day CZ hires). Most stop here and see gradual change. The few who transform everything upgrade to the Advanced course – 8 deeper modules with expanded guides, tasks, data-driven examples, AI integration, predictive analytics—or go all-in with the Masterclass (500+ pages, coaching, business-building for 50% faster hires/45% more seniors).",
      ctaText: "Sign Up for Advanced Waiting List",
      ctaLink: "#",
      features: [
        "Challenge Identification Form + Goal Setting Guide (advanced metrics)",
        "Theory: Advantages/disadvantages/methodology/pitfalls/market strategies/metrics/integration/improvement",
        "Case Studies: Competing with giants, niche sourcing, faster hires, templates, Q&A responses",
        "3 Tasks: 30-day plan for role, market-specific Q&A, metrics tracking system",
        "Advanced: AI-assisted planning (HireEZ), predictive analytics, market-responsive, talent communities"
      ]
    },
    {
      type: "consulting" as const,
      title: "Ready for your breakthrough?",
      description: "Book a free 30-minute audit. I'll review your challenges/plan (or from the Free kit) and show you – step by step – how Advanced or Masterclass changes everything.",
      ctaText: "BOOK FREE 30-MIN AUDIT – TRANSFORM YOUR HIRING FOREVER",
      ctaLink: "https://calendly.com/_abaddion/30min"
    }
  ],
  quizQuestions: [
    {
      id: 1,
      question: "Name a column in the Action Plan Template",
      options: ["Color", "Action", "Font", "Size"],
      correct: 1,
      explanation: "The Action Plan Template includes columns: Action, Owner, Deadline, and Outcome. Action is the specific task to be completed."
    },
    {
      id: 2,
      question: "How many actions should be in a 30-day plan for <60-day hires?",
      options: ["1 action", "3 actions", "10 actions", "20 actions"],
      correct: 1,
      explanation: "Build a 30-day plan with 3 actions for <60-day hires. Each action should have: Action/Owner/Deadline/Outcome."
    },
    {
      id: 3,
      question: "What's a CZ community mentioned for engaging seniors?",
      options: ["TikTok", "Prague Devs", "Instagram", "Pinterest"],
      correct: 1,
      explanation: "Prague Devs (500+ members) is an effective CZ community for engaging senior IT talent. 70% prefer hybrid/remote work."
    },
    {
      id: 4,
      question: "What drives >80% acceptance in the US?",
      options: ["In-office mandates", "Equity (0.2–0.5%)", "Long processes", "Low salaries"],
      correct: 1,
      explanation: "Equity (0.2–0.5%) and enterprise projects drive >80% acceptance in the US. Speed (72-hour offers) also helps beat Big Tech."
    },
    {
      id: 5,
      question: "What should you do at day 30 of your action plan?",
      options: ["Stop tracking", "Review and iterate", "Give up", "Start over"],
      correct: 1,
      explanation: "Review progress at day 30. What worked? What didn't? Iterate for the next 30 days. Learning without action = zero hires."
    }
  ]
};